import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { getReadableTitle } from './utils/titleUtils';
import { buildApiUrl } from './config';
import PartisanGauge from './PartisanGauge';
import { useDarkMode } from './DarkModeContext';
import { useNavigate } from 'react-router-dom';

// Add custom styles for timeline animations and scrollbars
const timelineStyles = `
  @keyframes pulse {
    0%, 100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }
  @keyframes ping {
    75%, 100% {
      transform: scale(2);
      opacity: 0;
    }
  }
  .animate-pulse {
    animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
  }
  .animate-ping {
    animation: ping 1s cubic-bezier(0, 0, 0.2, 1) infinite;
  }

  /* Custom Scrollbar Styles - Cross Browser */
  
  /* WebKit browsers (Chrome, Safari, Edge) */
  ::-webkit-scrollbar {
    width: 8px;
    height: 8px;
    -webkit-appearance: none;
  }

  ::-webkit-scrollbar-track {
    background: transparent;
    border-radius: 10px;
    border: none;
  }

  ::-webkit-scrollbar-thumb {
    background: linear-gradient(135deg, #3b82f6 0%, #ef4444 100%) !important;
    border-radius: 10px !important;
    border: 2px solid transparent !important;
    background-clip: content-box !important;
    transition: all 0.3s ease;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    -webkit-border-radius: 10px;
    -webkit-background-clip: content-box;
  }

  ::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(135deg, #2563eb 0%, #dc2626 100%);
    transform: scale(1.1);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  }

  ::-webkit-scrollbar-corner {
    background: transparent;
  }

  /* Safari specific overrides */
  @supports (-webkit-backdrop-filter: none) {
    ::-webkit-scrollbar {
      width: 8px !important;
      height: 8px !important;
    }
    
    ::-webkit-scrollbar-thumb {
      background: linear-gradient(135deg, #3b82f6 0%, #ef4444 100%) !important;
      border-radius: 10px !important;
      border: 2px solid transparent !important;
      background-clip: content-box !important;
      -webkit-background-clip: content-box !important;
      -webkit-appearance: none !important;
    }
    
    ::-webkit-scrollbar-thumb:hover {
      background: linear-gradient(135deg, #2563eb 0%, #dc2626 100%) !important;
    }
  }

  /* Dark mode scrollbar */
  .dark ::-webkit-scrollbar-thumb {
    background: linear-gradient(135deg, #60a5fa 0%, #f87171 100%);
  }

  .dark ::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(135deg, #3b82f6 0%, #ef4444 100%);
  }

  /* Firefox scrollbar */
  * {
    scrollbar-width: thin;
    scrollbar-color: #3b82f6 transparent;
  }

  .dark * {
    scrollbar-color: #60a5fa transparent;
  }

  /* Custom scrollbar for specific elements */
  .custom-scrollbar::-webkit-scrollbar {
    width: 6px;
    height: 6px;
    -webkit-appearance: none;
  }

  .custom-scrollbar::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.05);
    border-radius: 8px;
    border: none;
  }

  .custom-scrollbar::-webkit-scrollbar-thumb {
    background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
    border-radius: 8px;
    border: 1px solid transparent;
    background-clip: content-box;
    -webkit-background-clip: content-box;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    transition: all 0.3s ease;
  }

  .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(135deg, #e91e63 0%, #ff5722 100%);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
    transform: scale(1.05);
  }

  .dark .custom-scrollbar::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
  }

  .dark .custom-scrollbar::-webkit-scrollbar-thumb {
    background: linear-gradient(135deg, #ec4899 0%, #ef4444 100%);
  }

  .dark .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(135deg, #db2777 0%, #dc2626 100%);
  }

  /* Timeline specific scrollbar */
  .timeline-scrollbar::-webkit-scrollbar {
    width: 4px;
    height: 4px;
    -webkit-appearance: none;
  }

  .timeline-scrollbar::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.02);
    border-radius: 6px;
    border: none;
  }

  .timeline-scrollbar::-webkit-scrollbar-thumb {
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
    border-radius: 6px;
    border: 1px solid transparent;
    background-clip: content-box;
    -webkit-background-clip: content-box;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
    transition: all 0.3s ease;
  }

  .timeline-scrollbar::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(135deg, #059669 0%, #047857 100%);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    transform: scale(1.1);
  }

  .dark .timeline-scrollbar::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.02);
  }

  .dark .timeline-scrollbar::-webkit-scrollbar-thumb {
    background: linear-gradient(135deg, #34d399 0%, #10b981 100%);
  }

  .dark .timeline-scrollbar::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  }

  /* Modal scrollbar */
  .modal-scrollbar::-webkit-scrollbar {
    width: 8px;
    height: 8px;
    -webkit-appearance: none;
  }

  .modal-scrollbar::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.03);
    border-radius: 10px;
    border: none;
  }

  .modal-scrollbar::-webkit-scrollbar-thumb {
    background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
    border-radius: 10px;
    border: 2px solid transparent;
    background-clip: content-box;
    -webkit-background-clip: content-box;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    transition: all 0.3s ease;
  }

  .modal-scrollbar::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(135deg, #2563eb 0%, #1e40af 100%);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
    transform: scale(1.1);
  }

  .dark .modal-scrollbar::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.03);
  }

  .dark .modal-scrollbar::-webkit-scrollbar-thumb {
    background: linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%);
  }

  .dark .modal-scrollbar::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  }

  /* Force scrollbar styling in Safari */
  @media screen and (-webkit-min-device-pixel-ratio: 0) {
    ::-webkit-scrollbar {
      -webkit-appearance: none;
      width: 8px;
      height: 8px;
    }
    
    ::-webkit-scrollbar-thumb {
      -webkit-appearance: none;
      background: linear-gradient(135deg, #3b82f6 0%, #ef4444 100%);
      border-radius: 10px;
      border: 2px solid transparent;
      background-clip: content-box;
      -webkit-background-clip: content-box;
    }
    
    .custom-scrollbar::-webkit-scrollbar {
      -webkit-appearance: none;
      width: 6px;
      height: 6px;
    }
    
    .timeline-scrollbar::-webkit-scrollbar {
      -webkit-appearance: none;
      width: 4px;
      height: 4px;
    }
    
    .modal-scrollbar::-webkit-scrollbar {
      -webkit-appearance: none;
      width: 8px;
      height: 8px;
    }
  }

  /* Additional Safari-specific rules */
  @supports (-webkit-touch-callout: none) {
    ::-webkit-scrollbar {
      width: 8px !important;
      height: 8px !important;
      -webkit-appearance: none !important;
    }
    
    ::-webkit-scrollbar-thumb {
      background: linear-gradient(135deg, #3b82f6 0%, #ef4444 100%) !important;
      border-radius: 10px !important;
      border: 2px solid transparent !important;
      background-clip: content-box !important;
      -webkit-background-clip: content-box !important;
      -webkit-appearance: none !important;
    }
    
    .custom-scrollbar::-webkit-scrollbar {
      width: 6px !important;
      height: 6px !important;
      -webkit-appearance: none !important;
    }
    
    .custom-scrollbar::-webkit-scrollbar-thumb {
      background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%) !important;
      border-radius: 8px !important;
      border: 1px solid transparent !important;
      background-clip: content-box !important;
      -webkit-background-clip: content-box !important;
    }
    
    .timeline-scrollbar::-webkit-scrollbar {
      width: 4px !important;
      height: 4px !important;
      -webkit-appearance: none !important;
    }
    
    .timeline-scrollbar::-webkit-scrollbar-thumb {
      background: linear-gradient(135deg, #10b981 0%, #059669 100%) !important;
      border-radius: 6px !important;
      border: 1px solid transparent !important;
      background-clip: content-box !important;
      -webkit-background-clip: content-box !important;
    }
    
    .modal-scrollbar::-webkit-scrollbar {
      width: 8px !important;
      height: 8px !important;
      -webkit-appearance: none !important;
    }
    
    .modal-scrollbar::-webkit-scrollbar-thumb {
      background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%) !important;
      border-radius: 10px !important;
      border: 2px solid transparent !important;
      background-clip: content-box !important;
      -webkit-background-clip: content-box !important;
    }
  }

  /* Modal open/close animation */
  @keyframes modal-in {
    0% { opacity: 0; transform: scale(0.96) translateY(40px); }
    100% { opacity: 1; transform: scale(1) translateY(0); }
  }
  @keyframes modal-out {
    0% { opacity: 1; transform: scale(1) translateY(0); }
    100% { opacity: 0; transform: scale(0.96) translateY(40px); }
  }
`;
import { Search, FileText, Clock, Users, ArrowLeft, Calendar, ExternalLink, Tag, Filter, Moon, Sun, Eye, Scale, MapPin, Briefcase, GraduationCap, Heart, Shield, Zap, Leaf, Home, Target, Globe, Building, DollarSign, Car, Gavel, Vote, Hammer, Users2, Award, TreePine, Settings, HardHat, UserCheck, Plane, Laptop, Coins, Truck, ScrollText, CheckCircle, XCircle, RotateCcw, Skull, PenTool, Landmark, FileCheck, AlertCircle, RefreshCw, ClipboardList, Flag, Pause, ChevronDown } from 'lucide-react';

// Enhanced Custom Dropdown Component with Animations
const CustomDropdown = ({ 
  value, 
  onChange, 
  options, 
  placeholder, 
  isDarkMode, 
  icon: Icon,
  renderOption = null,
  className = ""
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (optionValue) => {
    onChange(optionValue);
    setIsOpen(false);
  };

  const selectedOption = options.find(opt => opt.value === value) || options[0];

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full flex items-center justify-between px-4 py-3 text-left border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 shadow-sm appearance-none cursor-pointer hover:shadow-md ${
          isDarkMode 
            ? 'bg-gray-700 border-gray-600 text-white hover:bg-gray-650 focus:bg-gray-650' 
            : 'bg-white border-gray-300 text-gray-900 hover:border-gray-400 focus:bg-gray-50'
        }`}
      >
        <div className="flex items-center">
          {Icon && <Icon className={`w-4 h-4 mr-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`} />}
          <span className="text-sm">{selectedOption?.label || placeholder}</span>
        </div>
        <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isDarkMode ? 'text-gray-300' : 'text-gray-500'} ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {/* Animated Dropdown Menu */}
      <div className={`absolute z-50 w-full mt-1 border rounded-xl shadow-lg max-h-60 overflow-y-auto transition-all duration-200 transform origin-top ${
        isOpen 
          ? 'opacity-100 scale-y-100' 
          : 'opacity-0 scale-y-95 pointer-events-none'
      } ${
        isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
      }`}>
        {options.map((option, index) => (
          <button
            key={option.value}
            onClick={() => handleSelect(option.value)}
            className={`w-full flex items-center px-4 py-3 text-left text-sm transition-all duration-150 ${
              isDarkMode 
                ? 'text-gray-200 hover:bg-gray-600 hover:text-white' 
                : 'text-gray-700 hover:bg-gray-100'
            } ${option.value === value ? (isDarkMode ? 'bg-gray-600 text-white' : 'bg-gray-100') : ''} ${
              isOpen ? 'animate-in slide-in-from-top-2 duration-200' : ''
            }`}
            style={{ animationDelay: `${index * 50}ms` }}
          >
            {renderOption ? renderOption(option) : (
              <>
                {option.icon && <option.icon className={`w-4 h-4 mr-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`} />}
                <span>{option.label}</span>
              </>
            )}
          </button>
        ))}
      </div>
    </div>
  );
};

const Bills = () => {
  const { isDarkMode, setIsDarkMode } = useDarkMode();
  const navigate = useNavigate();
  const [bills, setBills] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSession, setSelectedSession] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedPolicyTag, setSelectedPolicyTag] = useState('all');
  const [selectedBill, setSelectedBill] = useState(null);
  const [showModal, setShowModal] = useState(false);
  // Modal animation state
  const [modalVisible, setModalVisible] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [totalBills, setTotalBills] = useState(0);
  
  // Additional filters
  const [selectedBillType, setSelectedBillType] = useState('all');
  const [dateRange, setDateRange] = useState('all');
  const [sortBy, setSortBy] = useState('updated_desc');
  
  // Policy tags state
  const [allPolicyTags, setAllPolicyTags] = useState([]);



  // Request cancellation
  const abortControllerRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  // Memoize expensive calculations
  const memoizedGetCurrentBillStatus = useCallback((bill) => {
    if (!bill.history || bill.history.length === 0) {
      return { status: 'introduced', label: 'Introduced' };
    }

    const history = bill.history;
    
    // Check if this is a resolution
    const isResolution = bill.bill_number?.includes('R') && !bill.bill_number?.includes('B');
    
    // For bills, check if they passed both chambers
    const isHouseBill = bill.bill_number?.startsWith('HB') || bill.bill_number?.startsWith('HR');
    const isSenateBill = bill.bill_number?.startsWith('SB') || bill.bill_number?.startsWith('SR');
    const hasPassedHouse = history.some(h => h.action.toLowerCase().includes('house') && h.action.toLowerCase().includes('passed'));
    const hasPassedSenate = history.some(h => h.action.toLowerCase().includes('senate') && h.action.toLowerCase().includes('passed'));
    const passedBothChambers = (isHouseBill && hasPassedHouse && hasPassedSenate) || (isSenateBill && hasPassedHouse && hasPassedSenate);
    
    // Check ALL actions in history for final outcomes (not just the most recent)
    const hasBeenSigned = history.some(h => {
      const action = h.action.toLowerCase();
      return action.includes('signed by governor') || 
             action.includes('effective date') || 
             action.startsWith('act ');
    });
    
    // Check for resolution adoption - this is the final outcome for resolutions
    const hasBeenAdopted = history.some(h => {
      const action = h.action.toLowerCase();
      return action.includes('adopted') || 
             action.includes('read and adopted') ||
             (action.includes('read') && action.includes('adopted'));
    });
    
    const hasBeenVetoed = history.some(h => {
      const action = h.action.toLowerCase();
      return action.includes('veto') && !action.includes('override');
    });
    
    // More sophisticated withdrawal detection:
    // Find the most recent withdrawal action and check if there were any actions after it
    const withdrawalActions = [];
    for (let i = 0; i < history.length; i++) {
      const action = history[i].action.toLowerCase();
      if (action.includes('withdrawn')) {
        withdrawalActions.push({
          index: i,
          action: action,
          isWithdrawnAndRecommitted: action.includes('recommitted') || 
                                    action.includes('recommit') ||
                                    action.includes('referred back') ||
                                    action.includes('returned to committee')
        });
      }
    }
    
    // Determine current withdrawal status
    let isCurrentlyWithdrawn = false;
    let hasWithdrawnAndRecommitted = false;
    
    if (withdrawalActions.length > 0) {
      // Get the most recent withdrawal
      const lastWithdrawal = withdrawalActions[withdrawalActions.length - 1];
      
      if (lastWithdrawal.isWithdrawnAndRecommitted) {
        // Bill was withdrawn and recommitted in the same action
        hasWithdrawnAndRecommitted = true;
      } else {
        // Check if there are any substantial actions after the withdrawal
        const actionsAfterWithdrawal = history.slice(lastWithdrawal.index + 1);
        const hasSubstantialActionAfter = actionsAfterWithdrawal.some(h => {
          const action = h.action.toLowerCase();
          // Look for actions that indicate the bill is back in consideration
          return action.includes('referred') || 
                 action.includes('committee') ||
                 action.includes('passed') ||
                 action.includes('read') ||
                 action.includes('amended') ||
                 action.includes('motion') ||
                 action.includes('debate') ||
                 action.includes('vote') ||
                 action.includes('sent to') ||
                 action.includes('received');
        });
        
        if (hasSubstantialActionAfter) {
          // Bill was put back into consideration after withdrawal
          isCurrentlyWithdrawn = false;
        } else {
          // Bill remains withdrawn
          isCurrentlyWithdrawn = true;
        }
      }
    }
    
    const hasFailed = history.some(h => {
      const action = h.action.toLowerCase();
      return action.includes('failed') || 
             action.includes('defeated') ||
             action.includes('tabled indefinitely') ||
             action.includes('dead');
    });
    
    const hasVetoOverride = history.some(h => {
      const action = h.action.toLowerCase();
      return action.includes('veto') && action.includes('override');
    });
    
    // Return final outcomes first (these are terminal states)
    if (hasVetoOverride) {
      return { status: 'law', label: 'Veto Overridden' };
    }
    
    // Check if this is a joint resolution (goes to governor)
    const isJointResolution = bill.bill_number?.includes('JR');
    
    // Only bills and joint resolutions that pass both chambers and are signed become law
    if (hasBeenSigned && passedBothChambers) {
      return { status: 'law', label: 'Signed into Law' };
    }
    
    // For simple/concurrent resolutions, adoption = adopted (no governor involvement)
    if (isResolution && !isJointResolution && hasBeenAdopted) {
      return { status: 'adopted', label: 'Adopted' };
    }
    
    if (hasBeenVetoed) {
      return { status: 'vetoed', label: 'Vetoed' };
    }
    
    if (isCurrentlyWithdrawn) {
      return { status: 'withdrawn', label: 'Withdrawn' };
    }
    
    if (hasFailed) {
      return { status: 'failed', label: 'Dead/Failed' };
    }
    
    // If no final outcome, check current progress
    const recentAction = history[history.length - 1].action.toLowerCase();
    
    // Check for withdrawn and recommitted (still active in committee)
    if (hasWithdrawnAndRecommitted) {
      // Use existing chamber variables
      
      // Check if it has crossed chambers
      const hasSenatActions = history.some(h => h.action.toLowerCase().includes('senate'));
      const hasHouseActions = history.some(h => h.action.toLowerCase().includes('house'));
      const isInSecondChamber = (isHouseBill && hasSenatActions) || (isSenateeBill && hasHouseActions);
      
      if (isInSecondChamber) {
        return { status: 'secondChamberReview', label: `${isHouseBill ? 'Senate' : 'House'} Review` };
      } else {
        return { status: 'chamberReview', label: `${isHouseBill ? 'House' : isSenateeBill ? 'Senate' : 'Chamber'} Review` };
      }
    }
    
    // Check for governor's desk
    if (recentAction.includes('sent to governor') || 
        recentAction.includes('governor')) {
      return { status: 'governorDesk', label: "Governor's Desk" };
    }
    
    // Check for stalled/inactive status (bills with no recent activity)
    const mostRecentDate = bill.history && bill.history.length > 0 ? bill.history[bill.history.length - 1].date : bill.status_date;
    if (mostRecentDate) {
      const daysSinceLastAction = (Date.now() - new Date(mostRecentDate).getTime()) / (1000 * 60 * 60 * 24);
      
      // Check if bill has passed either chamber
      const hasPassedEitherChamber = history.some(h => {
        const action = h.action.toLowerCase();
        return action.includes('passed') || 
               action.includes('sent to') ||
               action.includes('read third time and passed') ||
               action.includes('adopted') ||
               (action.includes('sent to governor'));
      });
      
      // Check if bill got past introduction/first reading
      const hasCommitteeActivity = history.some(h => {
        const action = h.action.toLowerCase();
        return action.includes('committee') || 
               action.includes('referred to') ||
               action.includes('favorably reported') ||
               action.includes('committee substitute') ||
               action.includes('rules calendar') ||
               action.includes('second reading');
      });
      
      // Consider bill stalled if:
      // 1. No activity for 14+ days
      // 2. Hasn't passed either chamber
      // 3. Session appears to be ongoing (recent bills exist - simple heuristic)
      if (daysSinceLastAction >= 14 && !hasPassedEitherChamber) {
        // Unified inactive status - don't need to distinguish subcategories
        return { 
          status: 'stalled', 
          label: 'Inactive', 
          daysSinceAction: Math.floor(daysSinceLastAction)
        };
      }
    }
    
    // Use previously declared chamber variables
    
    // Check if bill has crossed to the second chamber by looking at action text
    const hasSenatActions = history.some(h => h.action.toLowerCase().includes('senate'));
    const hasHouseActions = history.some(h => h.action.toLowerCase().includes('house'));
    
    // Determine if we're in the second chamber
    const isInSecondChamber = (isHouseBill && hasSenatActions) || (isSenateeBill && hasHouseActions);
    
    if (isInSecondChamber) {
      // Check if still active in second chamber (not passed out yet)
      const recentActions = history.slice(-3).map(h => h.action.toLowerCase());
      const hasRecentSecondChamberActivity = isHouseBill 
x        ? recentActions.some(a => a.includes('senate'))
        : recentActions.some(a => a.includes('house'));
      
      if (hasRecentSecondChamberActivity) {
        return { status: 'secondChamberReview', label: `${isHouseBill ? 'Senate' : 'House'} Review` };
      }
    }
    
    // Default to first chamber review
    return { status: 'chamberReview', label: `${isHouseBill ? 'House' : isSenateeBill ? 'Senate' : 'Chamber'} Review` };
  }, []);

  // Helper to calculate days active for a bill
  const getDaysActive = useCallback((bill) => {
    if (!bill.history || bill.history.length === 0) return 0;
    const introducedDate = new Date(bill.history[0].date);
    // If bill is in a final status, use the date of the last action; otherwise, use today
    const finalStatuses = ['law', 'adopted', 'vetoed', 'withdrawn', 'failed'];
    const currentStatus = (bill.currentStatus && bill.currentStatus.status) || (typeof bill.currentStatus === 'string' ? bill.currentStatus : null);
    let endDate;
    if (finalStatuses.includes(currentStatus)) {
      endDate = new Date(bill.history[bill.history.length - 1].date);
    } else {
      endDate = new Date();
    }
    const diffTime = endDate - introducedDate;
    return Math.max(1, Math.ceil(diffTime / (1000 * 60 * 60 * 24)));
  }, []);

  // Memoize other expensive functions
  const memoizedGetMostRecentActionDate = useCallback((bill) => {
    if (!bill.history || bill.history.length === 0) {
      return bill.status_date || null;
    }
    return bill.history[bill.history.length - 1].date;
  }, []);

  useEffect(() => {
    fetchBills();
    fetchSessions();
    fetchAllPolicyTags();
  }, []);

  // Cleanup effect to cancel pending requests
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Separate search debouncing from other filter changes
  useEffect(() => {
    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Set new timeout for search
    searchTimeoutRef.current = setTimeout(() => {
      setCurrentPage(1);
      fetchBills(1);
    }, 800); // Increased from 300ms to 800ms

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm]);

  // Handle other filter changes (non-search)
  useEffect(() => {
    setCurrentPage(1);
    fetchBills(1);
  }, [selectedSession, selectedStatus, selectedPolicyTag, selectedBillType, dateRange, sortBy, itemsPerPage]);

  const fetchBills = async (page = 1, limit = itemsPerPage) => {
    try {
      // Cancel previous request if it exists
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      
      // Create new abort controller
      abortControllerRef.current = new AbortController();
      
      // Set appropriate loading state
      if (searchTerm && page === 1) {
        setSearchLoading(true);
      } else {
        setLoading(true);
      }
      
      // Build query parameters
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        search: searchTerm || '',
        session: selectedSession || 'all',
        status: selectedStatus || 'all',
        policyTag: selectedPolicyTag || 'all',
        billType: selectedBillType || 'all',
        dateRange: dateRange || 'all',
        sortBy: sortBy || 'updated_desc'
      });
      
      const response = await fetch(buildApiUrl(`/api/bills?${params}`), {
        signal: abortControllerRef.current.signal
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('📊 Loaded bills from API:', data);
      
      setBills(data.bills || data); // Handle both paginated and non-paginated responses
      setTotalBills(data.total || data.length || 0);
      setCurrentPage(page);
    } catch (err) {
      // Don't set error if request was cancelled
      if (err.name === 'AbortError') {
        console.log('Request cancelled');
        return;
      }
      console.error('❌ Error fetching bills:', err);
      setError('Failed to load bills. Please check if the server is running.');
    } finally {
      setLoading(false);
      setSearchLoading(false);
    }
  };

  const fetchSessions = async () => {
    try {
      const response = await fetch(buildApiUrl('/api/sessions'));
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('📋 Loaded sessions from API:', data);
      setSessions(data);
    } catch (err) {
      console.error('❌ Error fetching sessions:', err);
    }
  };

  const fetchAllPolicyTags = async () => {
    try {
      const response = await fetch(buildApiUrl('/api/policy-tags'));
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('🏷️ Loaded all policy tags from API:', data);
      // Extract the name property from each policy tag object
      const tagNames = data.map(tag => typeof tag === 'string' ? tag : tag.name).filter(Boolean);
      setAllPolicyTags(tagNames.sort());
    } catch (err) {
      console.error('❌ Error fetching policy tags:', err);
      // Fallback: extract from current bills if API fails
      const fallbackTags = [...new Set(bills.flatMap(bill => {
        if (!bill.policyTags) return [];
        return Array.isArray(bill.policyTags) 
          ? bill.policyTags.filter(Boolean)
          : [bill.policyTags].filter(Boolean);
      }))].filter(Boolean);
      setAllPolicyTags(fallbackTags.sort());
    }
  };

  const formatDate = useCallback((dateString) => {
    if (!dateString) return 'Unknown Date';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }, []);

  const getMostRecentActionDate = memoizedGetMostRecentActionDate;

  const getCurrentBillStatus = memoizedGetCurrentBillStatus;

  const getBillStatus = useCallback((status) => {
    const statusMap = {
      'chamberReview': 'House/Senate Review',
      'governorDesk': "Governor's Desk",
      'law': 'Signed into Law',
      'adopted': 'Adopted',
      'vetoed': 'Vetoed',
      'withdrawn': 'Withdrawn',
      'failed': 'Dead/Failed',
      'stalled': 'Inactive',
    };
    return statusMap[status] || status;
  }, []);

  const getStatusColor = useCallback((status) => {
    const statusColors = {
      'introduced': isDarkMode ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-100 text-blue-800',
      'chamberReview': isDarkMode ? 'bg-yellow-900/50 text-yellow-300' : 'bg-yellow-100 text-yellow-800',
      'secondChamberReview': isDarkMode ? 'bg-indigo-900/50 text-indigo-300' : 'bg-indigo-100 text-indigo-800',
      'inCommittee': isDarkMode ? 'bg-purple-900/50 text-purple-300' : 'bg-purple-100 text-purple-800',
      'governorDesk': isDarkMode ? 'bg-orange-900/50 text-orange-300' : 'bg-orange-100 text-orange-800',
      'law': isDarkMode ? 'bg-emerald-900/50 text-emerald-300' : 'bg-emerald-100 text-emerald-800',
      'adopted': isDarkMode ? 'bg-emerald-900/50 text-emerald-300' : 'bg-emerald-100 text-emerald-800',
      'vetoed': isDarkMode ? 'bg-red-900/50 text-red-300' : 'bg-red-100 text-red-800',
      'withdrawn': isDarkMode ? 'bg-gray-900/50 text-gray-300' : 'bg-gray-100 text-gray-800',
      'failed': isDarkMode ? 'bg-red-900/50 text-red-300' : 'bg-red-100 text-red-800',
      'stalled': isDarkMode ? 'bg-slate-900/50 text-slate-400' : 'bg-slate-200 text-slate-700'
    };
    return statusColors[status] || (isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-800');
  }, [isDarkMode]);

  const getPolicyTagColor = useCallback((tag) => {
    const colors = [
      isDarkMode ? 'bg-indigo-900/50 text-indigo-300' : 'bg-indigo-100 text-indigo-800',
      isDarkMode ? 'bg-pink-900/50 text-pink-300' : 'bg-pink-100 text-pink-800',
      isDarkMode ? 'bg-teal-900/50 text-teal-300' : 'bg-teal-100 text-teal-800',
      isDarkMode ? 'bg-amber-900/50 text-amber-300' : 'bg-amber-100 text-amber-800',
      isDarkMode ? 'bg-cyan-900/50 text-cyan-300' : 'bg-cyan-100 text-cyan-800'
    ];
    
    // Simple hash to get consistent color for same tag
    const hash = tag.split('').reduce((a, b) => {
      a = ((a << 5) - a) + b.charCodeAt(0);
      return a & a;
    }, 0);
    
    return colors[Math.abs(hash) % colors.length];
  }, [isDarkMode]);

  const getPolicyTagIcon = useCallback((tag) => {
    // Map each policy tag to a specific Lucide React icon
    const tagIcons = {
      'Agriculture Policy': TreePine,
      'Crime & Justice': Scale,
      'Disability': UserCheck,
      'Education': GraduationCap,
      'Energy': Zap,
      'Environment & Climate': Leaf,
      'Family & Social Issues': Heart,
      'Firearm Policy': Target,
      'Foreign Policy & Defense': Globe,
      'Healthcare': Heart,
      'Immigration & Border Security': MapPin,
      'International Trade': Plane,
      'Technology & Privacy': Laptop,
      'Taxes & Government Spending': DollarSign,
      'US Economy & Jobs': Briefcase,
      'Transportation & Infrastructure': Car,
      'Ceremonial & Honorary': Award,
      'Local Government & Municipal Affairs': Landmark,
      'Administrative & Regulatory': Settings,
      'Technical & Procedural Updates': FileText,
      'Government Operations': Building,
      'Professional Licensing & Regulation': UserCheck,
      'Elections & Voting': Vote,
      'Labor & Employment Rights': Users2,
      'Veterans Affairs': Shield,
      'Courts & Legal System': Scale,
      'Insurance & Financial Services': Coins,
      'Other': Tag
    };
    
    return tagIcons[tag] || Tag;
  }, []);

  // Create policy tag options with icons
  const policyTagOptions = useMemo(() => {
    const baseOptions = [
      { value: 'all', label: 'All Policy Tags', icon: Tag }
    ];
    
    const tagOptions = allPolicyTags.map(tag => ({
      value: tag,
      label: tag,
      icon: getPolicyTagIcon(tag)
    }));
    
    return [...baseOptions, ...tagOptions];
  }, [allPolicyTags, getPolicyTagIcon]);

  // Dropdown options
  const itemsPerPageOptions = [
    { value: 10, label: '10 bills' },
    { value: 25, label: '25 bills' },
    { value: 50, label: '50 bills' },
    { value: 100, label: '100 bills' },
    { value: 200, label: '200 bills' }
  ];

  const sortByOptions = [
    { value: 'updated_desc', label: 'Recently Updated' },
    { value: 'updated_asc', label: 'Oldest First' },
    { value: 'bill_number_asc', label: 'Bill Number (A-Z)' },
    { value: 'bill_number_desc', label: 'Bill Number (Z-A)' },
    { value: 'title_asc', label: 'Title (A-Z)' },
    { value: 'title_desc', label: 'Title (Z-A)' }
  ];

  const sessionOptions = useMemo(() => {
    const options = [
      { value: 'all', label: 'All Sessions' }
    ];
    
    sessions.forEach(session => {
      options.push({
        value: session.session_id.toString(),
        label: session.session_name
      });
    });
    
    return options;
  }, [sessions]);

  const statusOptions = [
    { value: 'all', label: 'All Statuses' },
    { value: 'law', label: 'Signed into Law' },
    { value: 'adopted', label: 'Adopted' },
    { value: 'governorDesk', label: "Governor's Desk" },
    { value: 'chamberReview', label: 'House/Senate Review' },
    { value: 'stalled', label: 'Inactive' },
    { value: 'vetoed', label: 'Vetoed' },
    { value: 'failed', label: 'Dead/Failed' },
    { value: 'withdrawn', label: 'Withdrawn' }
  ];

  const billTypeOptions = [
    { value: 'all', label: 'All Types' },
    { value: 'SB', label: 'Senate Bills' },
    { value: 'HB', label: 'House Bills' },
    { value: 'SR', label: 'Senate Resolutions' },
    { value: 'HR', label: 'House Resolutions' }
  ];

  const dateRangeOptions = [
    { value: 'all', label: 'All Time' },
    { value: 'week', label: 'Last Week' },
    { value: 'month', label: 'Last Month' },
    { value: '3months', label: 'Last 3 Months' },
    { value: 'year', label: 'Last Year' }
  ];

  const getPolicyTagEmoji = useCallback((tag) => {
    // Map each policy tag to a unique emoji for dropdown display
    const tagEmojis = {
      'Agriculture Policy': '🌾',
      'Crime & Justice': '⚖️',
      'Disability': '♿',
      'Education': '🎓',
      'Energy': '⚡',
      'Environment & Climate': '🌱',
      'Family & Social Issues': '👨‍👩‍👧‍👦',
      'Firearm Policy': '🎯',
      'Foreign Policy & Defense': '🌍',
      'Healthcare': '🏥',
      'Immigration & Border Security': '🛂',
      'International Trade': '✈️',
      'Technology & Privacy': '💻',
      'Taxes & Government Spending': '💰',
      'US Economy & Jobs': '💼',
      'Transportation & Infrastructure': '🚗',
      'Ceremonial & Honorary': '🏆',
      'Local Government & Municipal Affairs': '🏛️',
      'Administrative & Regulatory': '⚙️',
      'Technical & Procedural Updates': '📄',
      'Government Operations': '🏢',
      'Professional Licensing & Regulation': '👨‍💼',
      'Elections & Voting': '🗳️',
      'Labor & Employment Rights': '👷‍♂️',
      'Veterans Affairs': '🎖️',
      'Courts & Legal System': '⚖️',
      'Insurance & Financial Services': '🏦',
      'Other': '📂'
    };
    
    return tagEmojis[tag] || '📂';
  }, []);

  const getTimelineIcon = useCallback((emojiString) => {
    // Map emoji strings to Lucide React icon components
    const iconMap = {
      '🧾': ScrollText,
      '📄': FileText,
      '🏛️': Landmark,
      '✍️': PenTool,
      '⚖️': Scale,
      '✅': CheckCircle,
      '❌': XCircle,
      '↩️': RotateCcw,
      '💀': Skull,
      '📜': ScrollText,
      '🗳️': Vote,
      '💤': Pause
    };
    
    return iconMap[emojiString] || FileText;
  }, []);

  const truncateText = useCallback((text, maxLength = 150) => {
    if (!text || text.length <= maxLength) return text;
    const truncated = text.substring(0, maxLength);
    const lastSpaceIndex = truncated.lastIndexOf(' ');
    
    if (lastSpaceIndex > maxLength * 0.75) {
      return truncated.substring(0, lastSpaceIndex) + '...';
    }
    
    return truncated + '...';
  }, []);

  const handleBillClick = useCallback((bill) => {
    setSelectedBill(bill);
    setShowModal(true);
    setTimeout(() => setModalVisible(true), 10); // allow render before animating in
  }, []);

  const handleCloseModal = useCallback(() => {
    setModalVisible(false);
    setTimeout(() => {
      setShowModal(false);
      setSelectedBill(null);
    }, 250); // match animation duration
  }, []);

  // Memoize pagination calculations
  const paginationInfo = useMemo(() => {
  const totalPages = Math.ceil(totalBills / itemsPerPage);
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalBills);
    return { totalPages, startItem, endItem };
  }, [totalBills, itemsPerPage, currentPage]);

  // Helper functions for pagination
  const goToPage = useCallback((page) => {
    if (page >= 1 && page <= paginationInfo.totalPages) {
      fetchBills(page);
    }
  }, [paginationInfo.totalPages]);

  const handleItemsPerPageChange = useCallback((newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  }, []);

  // Memoize bill status calculations for the list
  const billsWithStatus = useMemo(() => {
    return bills.map(bill => {
      // Handle backend string status vs frontend object status
      let currentStatus;
      if (bill.currentStatus) {
        // Backend returns string, convert to object format
        if (typeof bill.currentStatus === 'string') {
          const statusMap = {
            'law': { status: 'law', label: 'Signed into Law' },
            'adopted': { status: 'adopted', label: 'Adopted' },
            'vetoed': { status: 'vetoed', label: 'Vetoed' },
            'withdrawn': { status: 'withdrawn', label: 'Withdrawn' },
            'failed': { status: 'failed', label: 'Dead/Failed' },
            'chamberReview': { status: 'chamberReview', label: 'House/Senate Review' },
            'secondChamberReview': { status: 'secondChamberReview', label: 'Second Chamber Review' },
            'governorDesk': { status: 'governorDesk', label: "Governor's Desk" },
            'stalled': { status: 'stalled', label: 'Inactive' },
            'introduced': { status: 'introduced', label: 'Introduced' }
          };
          currentStatus = statusMap[bill.currentStatus] || { status: bill.currentStatus, label: bill.currentStatus };
        } else {
          // Already an object (from frontend calculation)
          currentStatus = bill.currentStatus;
        }
      } else {
        // Fallback to frontend calculation
        currentStatus = getCurrentBillStatus(bill);
      }
      
      return {
        ...bill,
        currentStatus,
        mostRecentDate: getMostRecentActionDate(bill)
      };
    });
  }, [bills, getCurrentBillStatus, getMostRecentActionDate]);

  if (loading) {
    return (
      <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'} flex items-center justify-center`}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>Loading Georgia bills...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'} flex items-center justify-center`}>
        <div className="text-center">
          <FileText className={`w-16 h-16 ${isDarkMode ? 'text-gray-700' : 'text-gray-300'} mx-auto mb-4`} />
          <h3 className={`text-lg font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-900'} mb-2`}>Failed to load bills</h3>
          <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'} mb-4`}>{error}</p>
          <button 
            onClick={fetchBills}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{timelineStyles}</style>
      <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900 dark' : 'bg-gray-50'}`}>
      {/* Header */}
      <header className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-sm border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
        <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/')}
                className="flex items-center px-4 py-2 bg-gradient-to-r from-red-600 to-blue-600 hover:from-red-700 hover:to-blue-700 text-white rounded-lg transition-all duration-300 shadow-md hover:shadow-lg transform hover:-translate-x-1"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                <span>Back to Home</span>
              </button>
              <img src="/title.png" alt="CivicDuty Logo" className="h-10 w-auto cursor-pointer" style={{maxHeight: '3.5rem'}} onClick={() => navigate('/')} />
              <span className={`text-lg font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>• Georgia Bills</span>
            </div>
            <button
              onClick={() => setIsDarkMode(!isDarkMode)}
              className={`p-2 rounded-lg transition-all duration-200 ${
                isDarkMode 
                  ? 'bg-gray-700 text-yellow-400 hover:bg-gray-600' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              aria-label="Toggle dark mode"
            >
              {isDarkMode ? (
                <Sun className="w-5 h-5" />
              ) : (
                <Moon className="w-5 h-5" />
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Search and Filters */}
      <section className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Legislative Timeline - Prominent placement */}
          <div className="mb-8">
            <LegislativeTimeline isDarkMode={isDarkMode} />
          </div>

          <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl shadow-xl p-8 border ${isDarkMode ? 'border-gray-700' : 'border-gray-100'} mb-8`}>
            <div className="text-center mb-8">
              <div className={`inline-flex items-center justify-center w-12 h-12 rounded-full ${isDarkMode ? 'bg-gray-700' : 'bg-gradient-to-r from-red-100 to-blue-100'} mb-6`}>
                <FileText className={`w-6 h-6 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`} />
              </div>
              <h2 className={`text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'} mb-3`}>
                Georgia Legislative Bills
              </h2>
              <p className={`text-lg ${isDarkMode ? 'text-gray-300' : 'text-gray-600'} mb-2`}>
                Track legislative activity and bill progress
              </p>
              <p className={`text-base ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} mb-8`}>
                Showing {paginationInfo.startItem}-{paginationInfo.endItem} of {totalBills.toLocaleString()} bills
                {paginationInfo.totalPages > 1 && ` • Page ${currentPage} of ${paginationInfo.totalPages}`}
              </p>
            </div>

            {/* Search */}
            <div className="relative mb-6">
              <Search className={`absolute left-4 top-1/2 transform -translate-y-1/2 ${isDarkMode ? 'text-gray-400' : 'text-gray-400'} w-5 h-5`} />
              {searchLoading && (
                <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                </div>
              )}
              <input
                type="text"
                placeholder="Search by bill number, title, or description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                disabled={searchLoading}
                className={`w-full pl-12 pr-6 py-3 text-base border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all shadow-sm ${
                  isDarkMode 
                    ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                    : 'bg-white border-gray-200 text-gray-900 placeholder-gray-500'
                } ${searchLoading ? 'opacity-75 cursor-not-allowed' : ''}`}
              />
            </div>

            {/* Filters */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 mb-6">
              {/* Items per page selector */}
              <div>
                <label className={`flex items-center text-sm font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-3`}>
                  <Eye className="w-4 h-4 mr-2" />
                  Show per page
                </label>
                <CustomDropdown
                  value={itemsPerPage}
                  onChange={handleItemsPerPageChange}
                  options={itemsPerPageOptions}
                  placeholder="Select items per page"
                  isDarkMode={isDarkMode}
                  icon={Eye}
                />
              </div>

              {/* Sort by selector */}
              <div>
                <label className={`flex items-center text-sm font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-3`}>
                  <Filter className="w-4 h-4 mr-2" />
                  Sort by
                </label>
                <CustomDropdown
                  value={sortBy}
                  onChange={setSortBy}
                  options={sortByOptions}
                  placeholder="Select sort order"
                  isDarkMode={isDarkMode}
                  icon={Filter}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
              {/* Legislative Session Dropdown */}
              <div>
                <label className={`flex items-center text-sm font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-3`}>
                  <Calendar className="w-4 h-4 mr-2" />
                  Legislative Session
                </label>
                <CustomDropdown
                  value={selectedSession}
                  onChange={setSelectedSession}
                  options={sessionOptions}
                  placeholder="Select session"
                  isDarkMode={isDarkMode}
                  icon={Calendar}
                />
              </div>

              {/* Status Dropdown */}
              <div>
                <label className={`flex items-center text-sm font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-3`}>
                  <Scale className="w-4 h-4 mr-2" />
                  Bill Status
                </label>
                <CustomDropdown
                  value={selectedStatus}
                  onChange={setSelectedStatus}
                  options={statusOptions}
                  placeholder="Select status"
                  isDarkMode={isDarkMode}
                  icon={Scale}
                />
              </div>

              {/* Bill Type Dropdown */}
              <div>
                <label className={`flex items-center text-sm font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-3`}>
                  <FileText className="w-4 h-4 mr-2" />
                  Bill Type
                </label>
                <CustomDropdown
                  value={selectedBillType}
                  onChange={setSelectedBillType}
                  options={billTypeOptions}
                  placeholder="Select bill type"
                  isDarkMode={isDarkMode}
                  icon={FileText}
                />
              </div>

              {/* Date Range Dropdown */}
              <div>
                <label className={`flex items-center text-sm font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-3`}>
                  <Clock className="w-4 h-4 mr-2" />
                  Activity Period
                </label>
                <CustomDropdown
                  value={dateRange}
                  onChange={setDateRange}
                  options={dateRangeOptions}
                  placeholder="Select time period"
                  isDarkMode={isDarkMode}
                  icon={Clock}
                />
              </div>

              {/* Policy Tags Dropdown */}
              <div>
                <label className={`flex items-center text-sm font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-3`}>
                  <Tag className="w-4 h-4 mr-2" />
                  Policy Tags
                </label>
                <CustomDropdown
                  value={selectedPolicyTag}
                  onChange={setSelectedPolicyTag}
                  options={policyTagOptions}
                  placeholder="Select Policy Tag"
                  isDarkMode={isDarkMode}
                  icon={Tag}
                />
              </div>
            </div>

            {/* Active Filters Display */}
            {(selectedSession !== 'all' || selectedStatus !== 'all' || selectedPolicyTag !== 'all' || selectedBillType !== 'all' || dateRange !== 'all' || sortBy !== 'updated_desc') && (
              <div className="mt-6 flex flex-wrap items-center gap-2">
                <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Active filters:
                </span>
                {selectedSession !== 'all' && (
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${isDarkMode ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-100 text-blue-800'}`}>
                    <Calendar className="w-3 h-3 mr-1" />
                    {sessions.find(s => s.session_id === parseInt(selectedSession))?.session_name || 'Session'}
                    <button
                      onClick={() => setSelectedSession('all')}
                      className="ml-1 hover:text-blue-600"
                    >
                      ×
                    </button>
                  </span>
                )}
                {selectedStatus !== 'all' && (
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${isDarkMode ? 'bg-purple-900/50 text-purple-300' : 'bg-purple-100 text-purple-800'}`}>
                    <Scale className="w-3 h-3 mr-1" />
                    {getBillStatus(selectedStatus)}
                    <button
                      onClick={() => setSelectedStatus('all')}
                      className="ml-1 hover:text-purple-600"
                    >
                      ×
                    </button>
                  </span>
                )}
                {selectedPolicyTag !== 'all' && (
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${isDarkMode ? 'bg-green-900/50 text-green-300' : 'bg-green-100 text-green-800'}`}>
                    {React.createElement(getPolicyTagIcon(selectedPolicyTag), { className: "w-3 h-3 mr-1" })}
                    {selectedPolicyTag}
                    <button
                      onClick={() => setSelectedPolicyTag('all')}
                      className="ml-1 hover:text-green-600"
                    >
                      ×
                    </button>
                  </span>
                )}
                {selectedBillType !== 'all' && (
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${isDarkMode ? 'bg-teal-900/50 text-teal-300' : 'bg-teal-100 text-teal-800'}`}>
                    <FileText className="w-3 h-3 mr-1" />
                    {selectedBillType}
                    <button
                      onClick={() => setSelectedBillType('all')}
                      className="ml-1 hover:text-teal-600"
                    >
                      ×
                    </button>
                  </span>
                )}
                {dateRange !== 'all' && (
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${isDarkMode ? 'bg-amber-900/50 text-amber-300' : 'bg-amber-100 text-amber-800'}`}>
                    <Clock className="w-3 h-3 mr-1" />
                    {dateRange.charAt(0).toUpperCase() + dateRange.slice(1)}
                    <button
                      onClick={() => setDateRange('all')}
                      className="ml-1 hover:text-amber-600"
                    >
                      ×
                    </button>
                  </span>
                )}
                {sortBy !== 'updated_desc' && (
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${isDarkMode ? 'bg-indigo-900/50 text-indigo-300' : 'bg-indigo-100 text-indigo-800'}`}>
                    <Filter className="w-3 h-3 mr-1" />
                    {sortBy.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    <button
                      onClick={() => setSortBy('updated_desc')}
                      className="ml-1 hover:text-indigo-600"
                    >
                      ×
                    </button>
                  </span>
                )}
                <button
                  onClick={() => {
                    setSelectedSession('all');
                    setSelectedStatus('all');
                    setSelectedPolicyTag('all');
                    setSelectedBillType('all');
                    setDateRange('all');
                    setSortBy('updated_desc');
                  }}
                  className={`text-xs font-medium ${isDarkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'} underline`}
                >
                  Clear all filters
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Bills List */}
      <section className="pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-4">
            {billsWithStatus.map((bill) => (
              <BillCard 
                key={bill._id}
                bill={bill} 
                isDarkMode={isDarkMode} 
                onBillClick={handleBillClick} 
                formatDate={formatDate} 
                getStatusColor={getStatusColor} 
                getPolicyTagIcon={getPolicyTagIcon} 
                getPolicyTagColor={getPolicyTagColor}
                getDaysActive={getDaysActive}
              />
            ))}
          </div>

          {bills.length === 0 && !loading && (
            <div className="text-center py-12">
              <FileText className={`w-16 h-16 ${isDarkMode ? 'text-gray-600' : 'text-gray-300'} mx-auto mb-4`} />
              <h3 className={`text-lg font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-900'} mb-2`}>No bills found</h3>
              <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
                {searchTerm ? 'Try adjusting your search criteria or filters.' : 'No bills match your current filters.'}
              </p>
            </div>
          )}

          {/* Pagination Controls */}
          {paginationInfo.totalPages > 1 && (
            <div className={`mt-8 flex items-center justify-between ${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl p-6 shadow-sm`}>
              <div className="flex items-center space-x-2">
                <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Showing {paginationInfo.startItem}-{paginationInfo.endItem} of {totalBills.toLocaleString()} bills
                </span>
              </div>
              
              <div className="flex items-center space-x-2">
                {/* Previous button */}
                <button
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    currentPage === 1
                      ? isDarkMode ? 'bg-gray-700 text-gray-500 cursor-not-allowed' : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Previous
                </button>

                {/* Page numbers */}
                <div className="flex items-center space-x-1">
                  {/* First page */}
                  {currentPage > 3 && (
                    <>
                      <button
                        onClick={() => goToPage(1)}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        1
                      </button>
                      {currentPage > 4 && <span className={`px-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>...</span>}
                    </>
                  )}

                  {/* Current page range */}
                  {Array.from({ length: Math.min(5, paginationInfo.totalPages) }, (_, i) => {
                    const pageNum = Math.max(1, Math.min(paginationInfo.totalPages - 4, currentPage - 2)) + i;
                    if (pageNum > paginationInfo.totalPages) return null;
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => goToPage(pageNum)}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          pageNum === currentPage
                            ? 'bg-blue-600 text-white'
                            : isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}

                  {/* Last page */}
                  {currentPage < paginationInfo.totalPages - 2 && (
                    <>
                      {currentPage < paginationInfo.totalPages - 3 && <span className={`px-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>...</span>}
                      <button
                        onClick={() => goToPage(paginationInfo.totalPages)}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {paginationInfo.totalPages}
                      </button>
                    </>
                  )}
                </div>

                {/* Next button */}
                <button
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage === paginationInfo.totalPages}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    currentPage === paginationInfo.totalPages
                      ? isDarkMode ? 'bg-gray-700 text-gray-500 cursor-not-allowed' : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Bill Detail Modal */}
      {showModal && selectedBill && (
        <div className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 backdrop-blur-sm transition-opacity duration-300 ${modalVisible ? 'opacity-100' : 'opacity-0'}`}>
          <div className={`${isDarkMode ? 'bg-gray-800 dark' : 'bg-white'} rounded-2xl max-w-4xl w-full max-h-[90vh] shadow-2xl transform flex flex-col modal-scrollbar`}
            style={{
              animation: `${modalVisible ? 'modal-in' : 'modal-out'} 0.25s cubic-bezier(0.4,0,0.2,1) both`
            }}
          >
            {/* Modal Header */}
            <div className={`sticky top-0 z-20 ${isDarkMode ? 'bg-gray-800' : 'bg-white'} border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} p-6 rounded-t-2xl shadow-md backdrop-blur-sm`}>
              <div className="flex items-start justify-between gap-4 relative">
                {/* Left: Bill info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-3 mb-2">
                    <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{selectedBill.bill_number}</h2>
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(selectedBill.currentStatus.status)}`}>{selectedBill.currentStatus.label}</span>
                  </div>
                  <h3 className={`text-lg font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'} mb-2`}>{getReadableTitle(selectedBill)}</h3>
                  {/* Policy Tags in Modal */}
                  {selectedBill.policyTags && selectedBill.policyTags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-1">
                      {selectedBill.policyTags.map((tag, idx) => {
                        const IconComponent = getPolicyTagIcon(tag);
                        return (
                          <span key={idx} className={`inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-semibold ${getPolicyTagColor(tag)} shadow-sm border-2 border-transparent`}>
                            <IconComponent className="w-3 h-3 mr-1.5" />
                            {tag}
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>
                {/* Top right: Partisan Gauge and Close Button */}
                <div className="flex flex-col items-center justify-center flex-shrink-0" style={{ minWidth: 40, marginRight: 8, marginTop: 2, position: 'relative' }}>
                  {/* Close Button - right, vertically centered */}
                  <button
                    onClick={handleCloseModal}
                    className={`${isDarkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'} transition-colors p-2 ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'} rounded-full`}
                    style={{ position: 'absolute', right: -20, top: -8, zIndex: 10 }}
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            {/* Modal Content - Scrollable */}
            <div className="flex-1 overflow-y-auto modal-scrollbar">
              <div className="p-6 pt-4">
              {/* Bill Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6 items-end" style={{ minHeight: 110 }}>
                <div className={`p-4 rounded-lg h-full ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`} style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', height: '100%' }}>
                  <h4 className={`text-sm font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-2 uppercase tracking-wide`}>
                    Session Information
                  </h4>
                  <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-900'} font-medium`}>
                    {selectedBill.session_name}
                  </p>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} mt-1`}>
                    Last Action: {formatDate(selectedBill.mostRecentDate)}
                  </p>
                  {/* Show days active - Only for inactive bills */}
                  {(() => {
                    const currentStatus = (selectedBill.currentStatus && selectedBill.currentStatus.status) || (typeof selectedBill.currentStatus === 'string' ? selectedBill.currentStatus : null);
                    const inactiveStatuses = ['inactive', 'stalled'];
                    if (inactiveStatuses.includes(currentStatus)) {
                      return (
                        <p className={`text-sm ${isDarkMode ? 'text-blue-400' : 'text-blue-700'} mt-1`}>
                          ⏳ {getDaysActive(selectedBill)} days active
                        </p>
                      );
                    }
                    return null;
                  })()}
                  

                </div>

                <div className={`p-4 rounded-lg h-full ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`} style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', height: '100%' }}>
                  <h4 className={`text-sm font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-2 uppercase tracking-wide`}>
                    Sponsors ({selectedBill.sponsors?.length || 0})
                  </h4>
                  {selectedBill.sponsors && selectedBill.sponsors.length > 0 ? (
                    <div className="overflow-y-auto space-y-1 pr-2 custom-scrollbar" style={{ maxHeight: '72px' }}>
                      {selectedBill.sponsors.map((sponsor, idx) => (
                        <div key={idx} className="flex items-center space-x-2 flex-shrink-0">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                            sponsor.party === 'D' 
                              ? isDarkMode ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-800'
                              : sponsor.party === 'R'
                              ? isDarkMode ? 'bg-red-900 text-red-300' : 'bg-red-100 text-red-800'
                              : isDarkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-100 text-gray-700'
                          }`}>
                            {sponsor.party}
                          </span>
                          <span className={`${isDarkMode ? 'text-gray-300' : 'text-gray-900'} text-sm`}>
                            {sponsor.name}
                            {sponsor.sponsor_type_id === '1' && ' ★'}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      No sponsors listed
                    </p>
                  )}
                </div>
              </div>

              {/* Bill Summaries */}
              <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'} mb-6`}>
                <div className="flex items-center justify-between mb-3">
                  <h4 className={`text-sm font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} uppercase tracking-wide`}>
                    {selectedBill.ai_summary ? 'AI Summary' : 'Bill Description'}
                  </h4>
                  {selectedBill.ai_summary && selectedBill.ai_summary_detailed && (
                    <button
                      onClick={() => {
                        const summaryEl = document.getElementById(`modal-summary-${selectedBill._id}`);
                        const buttonEl = document.getElementById(`modal-toggle-${selectedBill._id}`);
                        const isShowingDetailed = summaryEl.textContent === selectedBill.ai_summary_detailed;
                        
                        if (isShowingDetailed) {
                          summaryEl.textContent = selectedBill.ai_summary;
                          buttonEl.textContent = 'Show Detailed Summary';
                        } else {
                          summaryEl.textContent = selectedBill.ai_summary_detailed;
                          buttonEl.textContent = 'Show Simple Summary';
                        }
                      }}
                      id={`modal-toggle-${selectedBill._id}`}
                      className={`text-xs ${isDarkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'} underline`}
                    >
                      Show Detailed Summary
                    </button>
                  )}
                </div>
                <p 
                  id={`modal-summary-${selectedBill._id}`}
                  className={`${isDarkMode ? 'text-gray-300' : 'text-gray-700'} leading-relaxed`}
                >
                  {selectedBill.ai_summary || selectedBill.description || 'No description available.'}
                </p>
                {selectedBill.ai_summary && selectedBill.description && (
                  <details className="mt-3">
                    <summary className={`text-sm cursor-pointer ${isDarkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-600 hover:text-gray-700'}`}>
                      Show original description
                    </summary>
                    <p className={`mt-2 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} leading-relaxed`}>
                      {selectedBill.description}
                    </p>
                  </details>
                )}
              </div>

              {/* Legislative Timeline */}
              {selectedBill.history && selectedBill.history.length > 0 && (
                <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'} mb-6`}>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className={`text-sm font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} uppercase tracking-wide`}>
                      Legislative Timeline ({selectedBill.history.length} actions)
                  </h4>
                    <button
                      onClick={() => {
                        const simplifiedEl = document.getElementById(`timeline-simplified-${selectedBill._id}`);
                        const detailsEl = document.getElementById(`timeline-details-${selectedBill._id}`);
                        const toggleText = document.getElementById(`toggle-text-${selectedBill._id}`);
                        const isShowingDetails = !detailsEl.classList.contains('hidden');
                        
                        if (isShowingDetails) {
                          detailsEl.classList.add('hidden');
                          simplifiedEl.classList.remove('hidden');
                          toggleText.textContent = 'Show Detailed View';
                        } else {
                          simplifiedEl.classList.add('hidden');
                          detailsEl.classList.remove('hidden');
                          toggleText.textContent = 'Show Simplified View';
                          
                          // Auto-scroll to bottom when switching to detailed view
                          setTimeout(() => {
                            const scrollContainer = detailsEl.querySelector('.max-h-80');
                            if (scrollContainer) {
                              scrollContainer.scrollTop = scrollContainer.scrollHeight;
                            }
                          }, 50);
                        }
                      }}
                      className={`text-xs ${isDarkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'} underline`}
                    >
                      <span id={`toggle-text-${selectedBill._id}`}>Show Detailed View</span>
                    </button>
                  </div>
                  
                  {(() => {
                    // Check if bill has reached final stage
                    const history = selectedBill.history || [];
                    const latestAction = history[history.length - 1]?.action.toLowerCase() || '';
                    const isFinalStage = latestAction.includes('signed by governor') || 
                                        latestAction.includes('effective date') ||
                                        latestAction.includes('vetoed') ||
                                        latestAction.includes('failed') ||
                                        latestAction.includes('defeated') ||
                                        latestAction.includes('tabled indefinitely');
                    
                    return (
                      <>
                                                {/* Simplified Timeline - Default View (Complete Timeline with Future Steps Grayed) */}
                        <div id={`timeline-simplified-${selectedBill._id}`} className="relative">
                          {/* Timeline line at the center of the icon (56px container) */}
                          <div className={`absolute left-[27px] top-0 bottom-0 w-0.5 ${isDarkMode ? 'bg-gray-600' : 'bg-gray-300'}`}></div>
                          {/* Timeline steps as flex-row */}
                          <div className="pr-2">
                            <div className="space-y-3 pb-2">
                              {(() => {
                                const history = selectedBill.history || [];
                                
                                // Get the current status from the same function used for the card
                                const billCurrentStatus = selectedBill.currentStatus;
                                
                                // Determine which chamber the bill originated in for display purposes
                                const isHouseBill = selectedBill.bill_number?.startsWith('HB') || selectedBill.bill_number?.startsWith('HR');
                                const isSenateeBill = selectedBill.bill_number?.startsWith('SB') || selectedBill.bill_number?.startsWith('SR');
                                
                                // Analyze bill history to determine timeline
                                const analyzeTimeline = () => {
                                  // Check if this is a resolution (single chamber process)
                                  const isResolution = selectedBill.bill_number?.includes('R') && !selectedBill.bill_number?.includes('B');
                                  
                                  if (isResolution) {
                                    // Analyze resolution timeline to determine type
                                    const analyzeResolutionType = () => {
                                      // Check for cross-chamber activity
                                      const hasSenatActions = history.some(h => h.action.toLowerCase().includes('senate'));
                                      const hasHouseActions = history.some(h => h.action.toLowerCase().includes('house'));
                                      const crossesChambers = (isHouseBill && hasSenatActions) || (isSenateeBill && hasHouseActions);
                                      
                                      // Check if this is a joint resolution (goes to governor)
                                      const isJointResolution = selectedBill.bill_number?.includes('JR');
                                      
                                      // Check for governor involvement
                                      const hasGovernorAction = history.some(h => {
                                        const action = h.action.toLowerCase();
                                        return action.includes('governor') || action.includes('signed') || action.includes('act ');
                                      });
                                      
                                      // Check content clues from title/description
                                      const titleText = (selectedBill.title || '').toLowerCase();
                                      const descText = (selectedBill.description || '').toLowerCase();
                                      const content = titleText + ' ' + descText;
                                      
                                      const isCeremonial = content.includes('honor') || content.includes('commend') || 
                                                          content.includes('recognize') || content.includes('congratulat') ||
                                                          content.includes('memorial day') || content.includes('awareness');
                                      
                                      const isSubstantive = content.includes('dedicate') || content.includes('create') || 
                                                           content.includes('establish') || content.includes('name') ||
                                                           content.includes('designat') || content.includes('commission');
                                      
                                      const isConstitutional = content.includes('constitution') || content.includes('amendment') ||
                                                              isJointResolution;
                                      
                                      // Determine resolution type based on evidence
                                      // Priority: Joint resolutions go to governor (like bills)
                                      if (isJointResolution) {
                                        return 'joint'; // Joint resolutions go to governor and become "Signed into Law"
                                      } else if (isConstitutional && !hasGovernorAction) {
                                        return 'constitutional'; // Both chambers, no governor, goes to voters
                                      } else if (crossesChambers || (isSubstantive && !isCeremonial)) {
                                        return 'substantive'; // Acts like a bill - both chambers + maybe governor
                                      } else {
                                        return 'simple'; // Single chamber ceremonial
                                      }
                                    };
                                    
                                    const resolutionType = analyzeResolutionType();
                                    
                                    // Return appropriate timeline based on resolution type
                                    switch (resolutionType) {
                                      case 'simple':
                                        // 3-step simple resolution timeline
                                        return [
                                          {
                                            key: 'introduced',
                                            stage: 'Introduced',
                                            icon: '🧾',
                                            baseColor: 'blue'
                                          },
                                          {
                                            key: 'chamberReview',
                                            stage: `${isHouseBill ? 'House' : isSenateeBill ? 'Senate' : 'Chamber'} Review`,
                                            icon: '🏛️',
                                            baseColor: 'yellow'
                                          },
                                          {
                                            key: 'outcome',
                                            stage: billCurrentStatus.status === 'failed' ? 'Failed'
                                              : billCurrentStatus.status === 'withdrawn' ? 'Withdrawn'
                                              : billCurrentStatus.status === 'adopted' ? 'Adopted'
                                              : 'Adopted',
                                            icon: billCurrentStatus.status === 'failed' ? '❌'
                                              : billCurrentStatus.status === 'withdrawn' ? '↩️'
                                              : '✅',
                                            baseColor: billCurrentStatus.status === 'failed' || billCurrentStatus.status === 'withdrawn' 
                                              ? 'red' : 'green'
                                          }
                                        ];
                                      
                                      case 'constitutional':
                                        // 4-step constitutional amendment timeline (no governor)
                                        return [
                                          {
                                            key: 'introduced',
                                            stage: 'Introduced',
                                            icon: '🧾',
                                            baseColor: 'blue'
                                          },
                                          {
                                            key: 'chamberReview',
                                            stage: `${isHouseBill ? 'House' : isSenateeBill ? 'Senate' : 'Chamber'} Review`,
                                            icon: '🏛️',
                                            baseColor: 'yellow'
                                          },
                                          {
                                            key: 'secondChamberReview',
                                            stage: `${isHouseBill ? 'Senate' : isSenateeBill ? 'House' : 'Second Chamber'} Review`,
                                            icon: '🏛️',
                                            baseColor: 'indigo'
                                          },
                                          {
                                            key: 'outcome',
                                            stage: billCurrentStatus.status === 'failed' ? 'Failed'
                                              : billCurrentStatus.status === 'withdrawn' ? 'Withdrawn'
                                              : billCurrentStatus.status === 'adopted' ? 'Sent to Voters'
                                              : 'Amendment Proposed',
                                            icon: billCurrentStatus.status === 'failed' ? '❌'
                                              : billCurrentStatus.status === 'withdrawn' ? '↩️'
                                              : '🗳️',
                                            baseColor: billCurrentStatus.status === 'failed' || billCurrentStatus.status === 'withdrawn' 
                                              ? 'red' : 'purple'
                                          }
                                        ];
                                      
                                      case 'joint':
                                        // Joint resolutions go to governor like bills
                                        return null; // Fall through to bill logic (5-step process)
                                      
                                      case 'substantive':
                                      default:
                                        // Use standard 5-step bill timeline for substantive resolutions
                                        return null; // Fall through to bill logic
                                    }
                                  }
                                  
                                  // Standard 5-step process for bills
                                  const completeProcess = [
                                    {
                                      key: 'introduced',
                                      stage: 'Introduced',
                                      icon: '📄',
                                      baseColor: 'blue'
                                    },
                                    {
                                      key: 'chamberReview',
                                      stage: `${isHouseBill ? 'House' : isSenateeBill ? 'Senate' : 'Chamber'} Review`,
                                      icon: '🏛️',
                                      baseColor: 'yellow'
                                    },
                                    {
                                      key: 'secondChamberReview',
                                      stage: `${isHouseBill ? 'Senate' : isSenateeBill ? 'House' : 'Second Chamber'} Review`,
                                      icon: '🏛️',
                                      baseColor: 'indigo'
                                    },
                                    {
                                      key: 'governorDesk',
                                      stage: "Governor's Desk",
                                      icon: '✍️',
                                      baseColor: 'orange'
                                    },
                                    {
                                      key: 'outcome',
                                      stage: 'Outcome',
                                      icon: '⚖️',
                                      baseColor: 'gray'
                                    }
                                  ];
                                  
                                  // Determine final outcome
                                  const getFinalOutcome = () => {
                                    switch (billCurrentStatus.status) {
                                      case 'law':
                                        return { stage: 'Signed into Law', icon: '📜', baseColor: 'green' };
                                      case 'adopted':
                                        return { stage: 'Adopted', icon: '✅', baseColor: 'green' };
                                      case 'vetoed':
                                        return { stage: 'Vetoed', icon: '❌', baseColor: 'red' };
                                      case 'withdrawn':
                                        return { stage: 'Withdrawn', icon: '↩️', baseColor: 'red' };
                                      case 'failed':
                                        return { stage: 'Dead/Failed', icon: '💀', baseColor: 'red' };
                                      default:
                                        return null;
                                    }
                                  };
                                  
                                  const finalOutcome = getFinalOutcome();
                                  
                                  // Check if bill died early or was withdrawn
                                  const hasSenatActions = history.some(h => h.action.toLowerCase().includes('senate'));
                                  const hasHouseActions = history.some(h => h.action.toLowerCase().includes('house'));
                                  const hasReachedSecondChamber = (isHouseBill && hasSenatActions) || (isSenateeBill && hasHouseActions);
                                  
                                  // Check if bill went to governor
                                  const hasGovernorAction = history.some(h => {
                                    const action = h.action.toLowerCase();
                                    return action.includes('signed by governor') || 
                                           action.includes('effective date') || 
                                           action.startsWith('act ') ||
                                           action.includes('sent to governor');
                                  });
                                  
                                  // Check if resolution was adopted without governor involvement
                                  const wasAdoptedWithoutGovernor = isResolution && 
                                    billCurrentStatus.status === 'adopted' && 
                                    !hasGovernorAction;
                                  
                                  const diedEarly = (billCurrentStatus.status === 'withdrawn' || billCurrentStatus.status === 'failed') && 
                                                   !hasReachedSecondChamber;
                                  
                                  // Build timeline steps based on actual bill progress
                                  let steps = [];
                                  
                                  // Always show introduction
                                  steps.push({ ...completeProcess[0] }); // Introduced
                                  
                                  // Always show first chamber review
                                  steps.push({ ...completeProcess[1] }); // First chamber review
                                  
                                  // Show second chamber only if bill actually reached it
                                  if (hasReachedSecondChamber) {
                                    steps.push({ ...completeProcess[2] }); // Second chamber review
                                  }
                                  
                                  // Show governor desk only if bill actually went to governor
                                  if (hasGovernorAction) {
                                    steps.push({ ...completeProcess[3] }); // Governor's desk
                                  }
                                  
                                  // Add final outcome
                                  if (finalOutcome) {
                                    steps.push({
                                      key: 'outcome',
                                      stage: finalOutcome.stage,
                                      icon: finalOutcome.icon,
                                      baseColor: finalOutcome.baseColor
                                    });
                                  }
                                  
                                  return steps;
                                };
                                
                                let timelineSteps = analyzeTimeline();
                                
                                // If resolution analysis returned null, use standard bill process
                                if (timelineSteps === null) {
                                  // This is a substantive resolution that acts like a bill
                                  const isResolution = selectedBill.bill_number?.includes('R') && !selectedBill.bill_number?.includes('B');
                                  
                                  // Standard 5-step process for substantive resolutions/bills
                                  const completeProcess = [
                                    {
                                      key: 'introduced',
                                      stage: 'Introduced',
                                      icon: isResolution ? '🧾' : '📄',
                                      baseColor: 'blue'
                                    },
                                    {
                                      key: 'chamberReview',
                                      stage: `${isHouseBill ? 'House' : isSenateeBill ? 'Senate' : 'Chamber'} Review`,
                                      icon: '🏛️',
                                      baseColor: 'yellow'
                                    },
                                    {
                                      key: 'secondChamberReview',
                                      stage: `${isHouseBill ? 'Senate' : isSenateeBill ? 'House' : 'Second Chamber'} Review`,
                                      icon: '🏛️',
                                      baseColor: 'indigo'
                                    },
                                    {
                                      key: 'governorDesk',
                                      stage: "Governor's Desk",
                                      icon: '✍️',
                                      baseColor: 'orange'
                                    },
                                    {
                                      key: 'outcome',
                                      stage: 'Outcome',
                                      icon: '⚖️',
                                      baseColor: 'gray'
                                    }
                                  ];
                                  
                                  // Determine final outcome
                                  const getFinalOutcome = () => {
                                    const isJointResolution = selectedBill.bill_number?.includes('JR');
                                    
                                    switch (billCurrentStatus.status) {
                                      case 'law':
                                        return { stage: 'Signed into Law', icon: '📜', baseColor: 'green' };
                                      case 'adopted':
                                        return { stage: 'Adopted', icon: '✅', baseColor: 'green' };
                                      case 'vetoed':
                                        return { stage: 'Vetoed', icon: '❌', baseColor: 'red' };
                                      case 'withdrawn':
                                        return { stage: 'Withdrawn', icon: '↩️', baseColor: 'red' };
                                      case 'failed':
                                        return { stage: 'Dead/Failed', icon: '💀', baseColor: 'red' };
                                      default:
                                        return null;
                                    }
                                  };
                                  
                                  const finalOutcome = getFinalOutcome();
                                  
                                  // Check if bill went to governor
                                  const hasGovernorAction = history.some(h => {
                                    const action = h.action.toLowerCase();
                                    return action.includes('signed by governor') || 
                                           action.includes('effective date') || 
                                           action.startsWith('act ') ||
                                           action.includes('sent to governor');
                                  });

                                  const hasSenatActions = history.some(h => h.action.toLowerCase().includes('senate'));
                                  const hasHouseActions = history.some(h => h.action.toLowerCase().includes('house'));
                                  const hasReachedSecondChamber = (isHouseBill && hasSenatActions) || (isSenateeBill && hasHouseActions);
                                  
                                  // Check if resolution was adopted without governor involvement
                                  const wasAdoptedWithoutGovernor = isResolution && 
                                    billCurrentStatus.status === 'adopted' && 
                                    !hasGovernorAction;
                                  
                                  const diedEarly = (billCurrentStatus.status === 'withdrawn' || billCurrentStatus.status === 'failed') && 
                                                   !hasReachedSecondChamber;
                                  
                                  // Build timeline steps based on actual bill progress
                                  let steps = [];
                                  
                                  // Always show introduction
                                  steps.push({ ...completeProcess[0] }); // Introduced
                                  
                                  // Always show first chamber review
                                  steps.push({ ...completeProcess[1] }); // First chamber review
                                  
                                  // Show second chamber only if bill actually reached it
                                  if (hasReachedSecondChamber) {
                                    steps.push({ ...completeProcess[2] }); // Second chamber review
                                  }
                                  
                                  // Show governor desk only if bill actually went to governor
                                  if (hasGovernorAction) {
                                    steps.push({ ...completeProcess[3] }); // Governor's desk
                                  }
                                  
                                  // Add final outcome
                                  if (finalOutcome) {
                                    steps.push({
                                      key: 'outcome',
                                      stage: finalOutcome.stage,
                                      icon: finalOutcome.icon,
                                      baseColor: finalOutcome.baseColor
                                    });
                                  }
                                  
                                  timelineSteps = steps;
                                }
                                
                                // Determine which step is current
                                const getCurrentStepIndex = () => {
                                  // Map current status to step keys
                                  const statusToStepKey = {
                                    'introduced': 'introduced',
                                    'chamberReview': 'chamberReview',
                                    'secondChamberReview': 'secondChamberReview',
                                    'governorDesk': 'governorDesk',
                                    'law': 'outcome',
                                    'adopted': 'outcome',
                                    'vetoed': 'outcome',
                                    'withdrawn': 'outcome',
                                    'failed': 'outcome',
                                    'stalled': 'chamberReview'
                                  };
                                  
                                  const currentStepKey = statusToStepKey[billCurrentStatus.status];
                                  return timelineSteps.findIndex(step => step.key === currentStepKey);
                                };
                                
                                const currentStepIndex = getCurrentStepIndex();
                                
                                // Function to get completion date for each step
                                const getStepCompletionDate = (stepKey) => {
                                  const isResolution = selectedBill.bill_number?.includes('R') && !selectedBill.bill_number?.includes('B');
                                  
                                  switch (stepKey) {
                                    case 'introduced':
                                      // Find the first action (introduction)
                                      return history.length > 0 ? history[0].date : null;
                                    
                                    case 'chamberReview':
                                      // Find when bill/resolution passed the FIRST chamber (originating chamber)
                                      const firstChamberActions = history.filter(h => {
                                        const action = h.action.toLowerCase();
                                        const isOriginatingChamber = (isHouseBill && action.includes('house')) || 
                                                                    (isSenateeBill && action.includes('senate'));
                                        return isOriginatingChamber && (action.includes('passed') || action.includes('adopted'));
                                      });
                                      return firstChamberActions.length > 0 ? firstChamberActions[0].date : null;
                                    
                                    case 'secondChamberReview':
                                      // Find when passed the SECOND chamber (opposite chamber)
                                      const secondChamberActions = history.filter(h => {
                                        const action = h.action.toLowerCase();
                                        const isSecondChamber = (isHouseBill && action.includes('senate')) || 
                                                              (isSenateeBill && action.includes('house'));
                                        return isSecondChamber && (action.includes('passed') || action.includes('adopted'));
                                      });
                                      return secondChamberActions.length > 0 ? secondChamberActions[0].date : null;
                                    
                                    case 'governorDesk':
                                      // Find when sent to governor
                                      const sentToGov = history.find(h => {
                                        const action = h.action.toLowerCase();
                                        return action.includes('sent to governor');
                                      });
                                      return sentToGov ? sentToGov.date : null;
                                    
                                    case 'outcome':
                                      // Find final outcome date - prioritize the most definitive action
                                      // Look for signed by governor first, then other outcomes
                                      const signedByGov = history.find(h => h.action.toLowerCase().includes('signed by governor'));
                                      if (signedByGov) return signedByGov.date;
                                      
                                      const effectiveDate = history.find(h => h.action.toLowerCase().includes('effective date'));
                                      if (effectiveDate) return effectiveDate.date;
                                      
                                      const vetoed = history.find(h => h.action.toLowerCase().includes('veto'));
                                      if (vetoed) return vetoed.date;
                                      
                                      const withdrawn = history.find(h => h.action.toLowerCase().includes('withdrawn'));
                                      if (withdrawn) return withdrawn.date;
                                      
                                      const failed = history.find(h => {
                                        const action = h.action.toLowerCase();
                                        return action.includes('failed') || action.includes('defeated');
                                      });
                                      if (failed) return failed.date;
                                      
                                      // For simple resolutions that only get adopted (no governor)
                                      if (isResolution) {
                                        const adopted = history.find(h => h.action.toLowerCase().includes('adopted'));
                                        if (adopted) return adopted.date;
                                      }
                                      
                                      return null;
                                    
                                    default:
                                      return null;
                                  }
                                };
                                
                                return timelineSteps.map((step, idx) => {
                                  const isCurrent = idx === currentStepIndex;
                                  const isCompleted = idx < currentStepIndex;
                                  const isFuture = idx > currentStepIndex;
                                  const completionDate = getStepCompletionDate(step.key);
                                  
                                  // Color logic
                                  const colorMap = {
                                    blue: {
                                      dotColor: 'bg-blue-500',
                                      badgeColor: isDarkMode ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-100 text-blue-800'
                                    },
                                    yellow: {
                                      dotColor: 'bg-yellow-500',
                                      badgeColor: isDarkMode ? 'bg-yellow-900/50 text-yellow-300' : 'bg-yellow-100 text-yellow-800'
                                    },
                                    purple: {
                                      dotColor: 'bg-purple-500',
                                      badgeColor: isDarkMode ? 'bg-purple-900/50 text-purple-300' : 'bg-purple-100 text-purple-800'
                                    },
                                    indigo: {
                                      dotColor: 'bg-indigo-500',
                                      badgeColor: isDarkMode ? 'bg-indigo-900/50 text-indigo-300' : 'bg-indigo-100 text-indigo-800'
                                    },
                                    orange: {
                                      dotColor: 'bg-orange-500',
                                      badgeColor: isDarkMode ? 'bg-orange-900/50 text-orange-300' : 'bg-orange-100 text-orange-800'
                                    },
                                    cyan: {
                                      dotColor: 'bg-cyan-500',
                                      badgeColor: isDarkMode ? 'bg-cyan-900/50 text-cyan-300' : 'bg-cyan-100 text-cyan-800'
                                    },
                                    green: {
                                      dotColor: 'bg-green-500',
                                      badgeColor: isDarkMode ? 'bg-green-900/50 text-green-300' : 'bg-green-100 text-green-800'
                                    },
                                    red: {
                                      dotColor: 'bg-red-500',
                                      badgeColor: isDarkMode ? 'bg-red-900/50 text-red-300' : 'bg-red-100 text-red-800'
                                    },
                                    gray: {
                                      dotColor: 'bg-gray-500',
                                      badgeColor: isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700'
                                    }
                                  };
                                  
                                  const colors = colorMap[step.baseColor];
                                  
                                  // Always use full color for badge and text for every step
                                  const stepColors = colors;
                                  
                                  return (
                                    <div key={idx} className={`flex items-start relative ${isCurrent ? 'current-step' : ''}`}>
                                      {/* Timeline dot with icon, fixed width, centered on the line */}
                                      <div className="relative flex items-center justify-center w-14 min-w-[56px]">
                                        {/* Mask to hide the line behind the circle */}
                                        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-7 h-7 rounded-full" style={{ background: isDarkMode ? '#1f2937' : '#fff', zIndex: 1 }}></div>
                                      <div className={`relative z-10 w-7 h-7 rounded-full ${stepColors.dotColor} border-2 ${isDarkMode ? 'border-gray-800' : 'border-white'} flex items-center justify-center text-white font-bold shadow-lg ${(isCompleted ? 'opacity-50' : '')} ${isFuture ? 'opacity-30' : ''}`}>
                                          {React.createElement(getTimelineIcon(step.icon), { 
                                            className: "w-4 h-4 animate-pulse" 
                                          })}
                                        {/* Pulsing ring animation for current step */}
                                        {isCurrent && (
                                          <div className={`absolute -inset-1 ${colors.dotColor} rounded-full opacity-75 animate-ping`}></div>
                                        )}
                                        </div>
                                      </div>
                                      
                                      <div className="ml-4 flex-1">
                                        <div className="flex items-center gap-2">
                                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-sm font-medium ${stepColors.badgeColor} ${isCompleted ? 'opacity-50' : ''}`}
                                            style={{
                                              background: stepColors.badgeColor?.includes('bg-') ? undefined : stepColors.badgeColor,
                                              color: stepColors.badgeColor?.includes('text-') ? undefined : stepColors.badgeColor?.split(' ')[1] || undefined
                                            }}>
                                            {step.stage}
                                          </span>
                                          {/* Show "Stalled" indicator if this is the current step and bill is stalled */}
                                          {isCurrent && billCurrentStatus.status === 'stalled' && (
                                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                              isDarkMode ? 'bg-amber-900/50 text-amber-300' : 'bg-amber-100 text-amber-700'
                                            }`}>
                                              Stalled
                                            </span>
                                          )}
                                        </div>
                                        {/* Show completion date for completed steps */}
                                        {(isCompleted || (isCurrent && completionDate)) && completionDate && (
                                          <div className={`text-xs mt-1 ml-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} ${isFuture ? 'opacity-30' : ''}`}>
                                            {step.key === 'introduced' ? formatDate(completionDate) : `Completed on ${formatDate(completionDate)}`}
                                          </div>
                                        )}
                                        {/* Show stall duration for stalled bills on current step */}
                                        {isCurrent && billCurrentStatus.status === 'stalled' && billCurrentStatus.daysSinceAction && (
                                          <div className={`text-xs mt-1 ml-2 ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`}>
                                            No activity for {billCurrentStatus.daysSinceAction} days
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  );
                                });
                              })()}
                            </div>
                          </div>
                        </div>

                        {/* Detailed Timeline (Hidden by default) - Shows all individual actions */}
                        <div id={`timeline-details-${selectedBill._id}`} className="hidden relative ml-4">
                    {/* Timeline line centered */}
                    <div className={`absolute left-[7px] top-0 bottom-0 w-0.5 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-300'}`}></div>
                    
                          <div 
                            className="max-h-80 overflow-y-auto pr-2 timeline-scrollbar"
                            ref={(el) => {
                              if (el) {
                                // Auto-scroll to bottom to show current action
                                setTimeout(() => {
                                  el.scrollTop = el.scrollHeight;
                                }, 100);
                              }
                            }}
                          >
                            <div className="space-y-4 pb-4">
                              {selectedBill.history.map((entry, idx) => (
                        <div key={idx} className="flex items-start relative">
                          {/* Timeline dot */}
                          <div className="relative flex items-center justify-center w-4 min-w-[16px] mt-1.5">
                            <div className={`z-10 w-3 h-3 rounded-full border-2 ${isDarkMode ? 'border-gray-800' : 'border-white'} ${
                                    idx === selectedBill.history.length - 1 
                                      ? isFinalStage 
                                        ? 'bg-green-500' // Solid for final stage
                                        : 'bg-blue-500 animate-pulse' // Pulsing for ongoing
                              : isDarkMode ? 'bg-gray-600' : 'bg-gray-400'
                                  }`}>
                                    {/* Glowing ring for current status (only if not final stage) */}
                                    {idx === selectedBill.history.length - 1 && !isFinalStage && (
                                      <div className="absolute -inset-1 bg-blue-500 rounded-full opacity-75 animate-ping"></div>
                            )}
                            </div>
                          </div>
                          
                          <div className="ml-4 flex-1 pb-2">
                                    <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-900'} font-medium text-sm`}>
                              {entry.action}
                            </p>
                                    <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} mt-1`}>
                              {formatDate(entry.date)}
                              {entry.chamber_name && ` • ${entry.chamber_name}`}
                            </p>
                                    {idx === selectedBill.history.length - 1 && (
                              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium mt-2 ${
                                        isFinalStage
                                          ? isDarkMode ? 'bg-green-900/50 text-green-300' : 'bg-green-100 text-green-800'
                                          : isDarkMode ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-100 text-blue-800'
                              }`}>
                                        {isFinalStage ? 'Final Status' : 'Current Status'}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                        </div>
                      </>
                    );
                  })()}
                </div>
              )}

              {/* Voting Record */}
              {selectedBill.votes && selectedBill.votes.length > 0 && (
                <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'} mb-6`}>
                  <h4 className={`text-sm font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-3 uppercase tracking-wide`}>
                    Voting Record ({selectedBill.votes.length})
                  </h4>
                  <div className="space-y-4">
                    {selectedBill.votes.slice().reverse().map((vote, idx) => (
                      <div key={idx} className={`border rounded-lg p-3 ${isDarkMode ? 'border-gray-600 bg-gray-800' : 'border-gray-200 bg-white'}`}>
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-1">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                vote.passed 
                                  ? isDarkMode ? 'bg-green-900/50 text-green-300' : 'bg-green-100 text-green-800'
                                  : isDarkMode ? 'bg-red-900/50 text-red-300' : 'bg-red-100 text-red-800'
                              }`}>
                                {vote.passed ? '✓ PASSED' : '✗ FAILED'}
                              </span>
                              <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                {formatDate(vote.date)}
                              </span>
                              {vote.chamber_name && (
                                <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                  • {vote.chamber_name}
                                </span>
                              )}
                            </div>
                            {vote.description && (
                              <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                                {vote.description}
                              </p>
                            )}
                            {vote.motion && (
                              <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} italic`}>
                                Motion: {vote.motion}
                              </p>
                            )}
                          </div>
                        </div>
                        
                        {/* Vote Breakdown with Visual Bar */}
                        <div className="mt-3">
                          {/* Vote Numbers Grid with Hover Tooltips */}
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
                            {/* Yea Votes */}
                            <div className="text-center relative group">
                              <div className={`text-lg font-bold ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                                {vote.yea}
                              </div>
                              <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} uppercase tracking-wide`}>
                                Yea ({((vote.yea / vote.total) * 100).toFixed(1)}%)
                              </div>
                              
                              {/* Party Breakdown Tooltip for Yea */}
                              {(() => {
                                const matchingBreakdown = selectedBill.voteBreakdowns && vote.roll_call_id
                                  ? selectedBill.voteBreakdowns.find(bd => bd.rollCallId === vote.roll_call_id)
                                  : null;
                                
                                if (!matchingBreakdown?.partyVoteCounts) return null;
                                
                                const yeaBreakdown = {
                                  Democrat: matchingBreakdown.partyVoteCounts.Democrat?.Yea || 0,
                                  Republican: matchingBreakdown.partyVoteCounts.Republican?.Yea || 0,
                                  Other: matchingBreakdown.partyVoteCounts.Other?.Yea || 0
                                };
                                
                                return (
                                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50">
                                    <div className={`px-3 py-2 rounded-lg shadow-lg border ${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'}`}>
                                      <div className="text-xs font-semibold mb-1 text-center">Yea Votes by Party</div>
                                      <div className="space-y-1">
                                        {Object.entries(yeaBreakdown).map(([party, count]) => (
                                          <div key={party} className="flex justify-between items-center text-xs">
                                            <span className={`inline-flex items-center justify-center w-24 px-2 py-0.5 rounded text-xs font-medium ${
                                              party === 'Democrat' ? 'bg-blue-100 text-blue-800' :
                                              party === 'Republican' ? 'bg-red-100 text-red-800' :
                                              'bg-gray-100 text-gray-800'
                                            }`}>
                                              {party}
                                            </span>
                                            {(() => {
                                              const partyCounts = matchingBreakdown.partyVoteCounts[party] || {};
                                              const partyTotal = 
                                                (partyCounts.Yea || 0) +
                                                (partyCounts.Nay || 0) +
                                                (partyCounts.NV || 0) +
                                                (partyCounts.Absent || 0);
                                              const percent = partyTotal > 0 ? ((count / partyTotal) * 100).toFixed(1) : 'N/A';
                                              return (
                                                <>
                                                  <span className="font-bold ml-2">{count}</span>
                                                  <span className="ml-1 text-xs text-gray-400">
                                                    ({percent === 'N/A' ? 'N/A' : percent + '%'})
                                                  </span>
                                                </>
                                              );
                                            })()}
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                    {/* Arrow */}
                                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800 dark:border-t-gray-800"></div>
                                  </div>
                                );
                              })()}
                            </div>
                            
                            {/* Nay Votes */}
                            <div className="text-center relative group">
                              <div className={`text-lg font-bold ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>
                                {vote.nay}
                              </div>
                              <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} uppercase tracking-wide`}>
                                Nay ({((vote.nay / vote.total) * 100).toFixed(1)}%)
                              </div>
                              
                              {/* Party Breakdown Tooltip for Nay */}
                              {(() => {
                                const matchingBreakdown = selectedBill.voteBreakdowns && vote.roll_call_id
                                  ? selectedBill.voteBreakdowns.find(bd => bd.rollCallId === vote.roll_call_id)
                                  : null;
                                
                                if (!matchingBreakdown?.partyVoteCounts) return null;
                                
                                const nayBreakdown = {
                                  Democrat: matchingBreakdown.partyVoteCounts.Democrat?.Nay || 0,
                                  Republican: matchingBreakdown.partyVoteCounts.Republican?.Nay || 0,
                                  Other: matchingBreakdown.partyVoteCounts.Other?.Nay || 0
                                };
                                
                                return (
                                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50">
                                    <div className={`px-3 py-2 rounded-lg shadow-lg border ${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'}`}>
                                      <div className="text-xs font-semibold mb-1 text-center">Nay Votes by Party</div>
                                      <div className="space-y-1">
                                        {Object.entries(nayBreakdown).map(([party, count]) => (
                                          <div key={party} className="flex justify-between items-center text-xs">
                                            <span className={`inline-flex items-center justify-center w-24 px-2 py-0.5 rounded text-xs font-medium ${
                                              party === 'Democrat' ? 'bg-blue-100 text-blue-800' :
                                              party === 'Republican' ? 'bg-red-100 text-red-800' :
                                              'bg-gray-100 text-gray-800'
                                            }`}>
                                              {party}
                                            </span>
                                            {(() => {
                                              const partyCounts = matchingBreakdown.partyVoteCounts[party] || {};
                                              const partyTotal = 
                                                (partyCounts.Yea || 0) +
                                                (partyCounts.Nay || 0) +
                                                (partyCounts.NV || 0) +
                                                (partyCounts.Absent || 0);
                                              const percent = partyTotal > 0 ? ((count / partyTotal) * 100).toFixed(1) : 'N/A';
                                              return (
                                                <>
                                                  <span className="font-bold ml-2">{count}</span>
                                                  <span className="ml-1 text-xs text-gray-400">
                                                    ({percent === 'N/A' ? 'N/A' : percent + '%'})
                                                  </span>
                                                </>
                                              );
                                            })()}
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                    {/* Arrow */}
                                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800 dark:border-t-gray-800"></div>
                                  </div>
                                );
                              })()}
                            </div>
                            
                            {/* No Vote */}
                            <div className="text-center relative group">
                              <div className={`text-lg font-bold ${isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>
                                {vote.nv || 0}
                              </div>
                              <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} uppercase tracking-wide`}>
                                No Vote ({(((vote.nv || 0) / vote.total) * 100).toFixed(1)}%)
                              </div>
                              
                              {/* Party Breakdown Tooltip for No Vote */}
                              {(() => {
                                const matchingBreakdown = selectedBill.voteBreakdowns && vote.roll_call_id
                                  ? selectedBill.voteBreakdowns.find(bd => bd.rollCallId === vote.roll_call_id)
                                  : null;
                                
                                if (!matchingBreakdown?.partyVoteCounts) return null;
                                
                                const nvBreakdown = {
                                  Democrat: matchingBreakdown.partyVoteCounts.Democrat?.NV || 0,
                                  Republican: matchingBreakdown.partyVoteCounts.Republican?.NV || 0,
                                  Other: matchingBreakdown.partyVoteCounts.Other?.NV || 0
                                };
                                
                                return (
                                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50">
                                    <div className={`px-3 py-2 rounded-lg shadow-lg border ${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'}`}>
                                      <div className="text-xs font-semibold mb-1 text-center">No Votes by Party</div>
                                      <div className="space-y-1">
                                        {Object.entries(nvBreakdown).map(([party, count]) => (
                                          <div key={party} className="flex justify-between items-center text-xs">
                                            <span className={`inline-flex items-center justify-center w-24 px-2 py-0.5 rounded text-xs font-medium ${
                                              party === 'Democrat' ? 'bg-blue-100 text-blue-800' :
                                              party === 'Republican' ? 'bg-red-100 text-red-800' :
                                              'bg-gray-100 text-gray-800'
                                            }`}>
                                              {party}
                                            </span>
                                            {(() => {
                                              const partyCounts = matchingBreakdown.partyVoteCounts[party] || {};
                                              const partyTotal = 
                                                (partyCounts.Yea || 0) +
                                                (partyCounts.Nay || 0) +
                                                (partyCounts.NV || 0) +
                                                (partyCounts.Absent || 0);
                                              const percent = partyTotal > 0 ? ((count / partyTotal) * 100).toFixed(1) : 'N/A';
                                              return (
                                                <>
                                                  <span className="font-bold ml-2">{count}</span>
                                                  <span className="ml-1 text-xs text-gray-400">
                                                    ({percent === 'N/A' ? 'N/A' : percent + '%'})
                                                  </span>
                                                </>
                                              );
                                            })()}
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                    {/* Arrow */}
                                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800 dark:border-t-gray-800"></div>
                                  </div>
                                );
                              })()}
                            </div>
                            
                            {/* Absent */}
                            <div className="text-center relative group">
                              <div className={`text-lg font-bold ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                {vote.absent || 0}
                              </div>
                              <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} uppercase tracking-wide`}>
                                Absent ({(((vote.absent || 0) / vote.total) * 100).toFixed(1)}%)
                              </div>
                              
                              {/* Party Breakdown Tooltip for Absent */}
                              {(() => {
                                const matchingBreakdown = selectedBill.voteBreakdowns && vote.roll_call_id
                                  ? selectedBill.voteBreakdowns.find(bd => bd.rollCallId === vote.roll_call_id)
                                  : null;
                                
                                if (!matchingBreakdown?.partyVoteCounts) return null;
                                
                                const absentBreakdown = {
                                  Democrat: matchingBreakdown.partyVoteCounts.Democrat?.Absent || 0,
                                  Republican: matchingBreakdown.partyVoteCounts.Republican?.Absent || 0,
                                  Other: matchingBreakdown.partyVoteCounts.Other?.Absent || 0
                                };
                                
                                return (
                                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50">
                                    <div className={`px-3 py-2 rounded-lg shadow-lg border ${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'}`}>
                                      <div className="text-xs font-semibold mb-1 text-center">Absent by Party</div>
                                      <div className="space-y-1">
                                        {Object.entries(absentBreakdown).map(([party, count]) => (
                                          <div key={party} className="flex justify-between items-center text-xs">
                                            <span className={`inline-flex items-center justify-center w-24 px-2 py-0.5 rounded text-xs font-medium ${
                                              party === 'Democrat' ? 'bg-blue-100 text-blue-800' :
                                              party === 'Republican' ? 'bg-red-100 text-red-800' :
                                              'bg-gray-100 text-gray-800'
                                            }`}>
                                              {party}
                                            </span>
                                            {(() => {
                                              const partyCounts = matchingBreakdown.partyVoteCounts[party] || {};
                                              const partyTotal = 
                                                (partyCounts.Yea || 0) +
                                                (partyCounts.Nay || 0) +
                                                (partyCounts.NV || 0) +
                                                (partyCounts.Absent || 0);
                                              const percent = partyTotal > 0 ? ((count / partyTotal) * 100).toFixed(1) : 'N/A';
                                              return (
                                                <>
                                                  <span className="font-bold ml-2">{count}</span>
                                                  <span className="ml-1 text-xs text-gray-400">
                                                    ({percent === 'N/A' ? 'N/A' : percent + '%'})
                                                  </span>
                                                </>
                                              );
                                            })()}
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                    {/* Arrow */}
                                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800 dark:border-t-gray-800"></div>
                                  </div>
                                );
                              })()}
                            </div>
                          </div>

                          {/* Visual Vote Bar */}
                          <div className="relative mt-12">
                            <div className={`w-full h-6 rounded-lg overflow-hidden ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'} flex`}>
                              {/* Yes votes */}
                              <div 
                                className="bg-green-500 flex items-center justify-center text-xs font-medium text-white"
                                style={{ width: `${(vote.yea / vote.total) * 100}%` }}
                              >
                              </div>
                              {/* No votes */}
                              <div 
                                className="bg-red-500 flex items-center justify-center text-xs font-medium text-white"
                                style={{ width: `${(vote.nay / vote.total) * 100}%` }}
                              >
                              </div>
                              {/* No votes / abstentions */}
                              {(vote.nv || 0) > 0 && (
                                <div 
                                  className="bg-yellow-500 flex items-center justify-center text-xs font-medium text-white"
                                  style={{ width: `${((vote.nv || 0) / vote.total) * 100}%` }}
                                >
                                </div>
                              )}
                              {/* Absent */}
                              {(vote.absent || 0) > 0 && (
                                <div 
                                  className={`${isDarkMode ? 'bg-gray-600' : 'bg-gray-400'} flex items-center justify-center text-xs font-medium text-white`}
                                  style={{ width: `${((vote.absent || 0) / vote.total) * 100}%` }}
                                >
                                </div>
                              )}
                            </div>
                            
                            {/* Minimal threshold tick above the bar */}
                            {(() => {
                              // Calculate Georgia-specific threshold
                              const getGeorgiaThreshold = (bill, vote) => {
                                const billNumber = bill.bill_number || '';
                                const billTitle = (bill.title || '').toLowerCase();
                                const billDescription = (bill.description || '').toLowerCase();
                                const voteDescription = (vote.description || '').toLowerCase();
                                
                                const isConstitutionalAmendment = 
                                  billNumber.includes('JR') || 
                                  billTitle.includes('constitution') || 
                                  billTitle.includes('amend') ||
                                  billDescription.includes('constitution') ||
                                  billDescription.includes('constitutional amendment');
                                
                                const isVetoOverride = 
                                  voteDescription.includes('override') ||
                                  voteDescription.includes('veto');
                                
                                // Determine chamber and full membership
                                let fullMembership;
                                let chamberName;
                                if (vote.chamber_name) {
                                  chamberName = vote.chamber_name.toLowerCase();
                                } else {
                                  if (billNumber.startsWith('H')) {
                                    chamberName = 'house';
                                  } else if (billNumber.startsWith('S')) {
                                    chamberName = 'senate';
                                  } else {
                                    chamberName = 'unknown';
                                  }
                                }
                                
                                if (chamberName.includes('house')) {
                                  fullMembership = 180;
                                } else if (chamberName.includes('senate')) {
                                  fullMembership = 56;
                                } else {
                                  fullMembership = vote.total;
                                }
                                
                                // Calculate present and voting (excluding absent and no vote)
                                const presentAndVoting = vote.yea + vote.nay;
                                
                                let requiredVotes;
                                if (isConstitutionalAmendment) {
                                  // Constitutional amendments: 2/3 of FULL MEMBERSHIP
                                  requiredVotes = Math.ceil(fullMembership * 2/3);
                                } else if (isVetoOverride) {
                                  // Veto overrides: 2/3 of those PRESENT AND VOTING
                                  requiredVotes = Math.ceil(presentAndVoting * 2/3);
                                } else {
                                  // Regular votes: majority (more than 50%) of those PRESENT AND VOTING
                                  requiredVotes = Math.floor(presentAndVoting / 2) + 1;
                                }
                                
                                return requiredVotes;
                              };
                              
                              const georgiaThreshold = getGeorgiaThreshold(selectedBill, vote);
                              const thresholdToUse = vote.threshold || georgiaThreshold;
                              
                              return (
                                <div 
                                  className="absolute -top-10 w-3 h-10 z-10 flex flex-col items-center justify-end"
                                  style={{ left: `${(thresholdToUse / vote.total) * 100}%`, transform: 'translateX(-50%)' }}
                                >
                                  {/* Required box above the tick */}
                                  <div className={`mb-1 px-2 py-1 rounded-md shadow-lg text-xs font-bold whitespace-nowrap ${
                                    vote.passed 
                                      ? 'bg-green-600 text-white shadow-green-500/50'
                                      : 'bg-red-600 text-white shadow-red-500/50'
                                  }`}>
                                    Required: {thresholdToUse} votes
                                  </div>
                                  {/* Minimal tick */}
                                  <div className={`w-0.5 h-3 rounded-full ${vote.passed ? 'bg-green-600' : 'bg-red-600'}`}></div>
                                </div>
                              );
                            })()}
                          </div>
                          

                        </div>

                        {/* Vote Total and Threshold */}
                        <div className="flex items-center justify-between mt-3 pt-2 border-t border-gray-200 dark:border-gray-600">
                          <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Total: {vote.total}
                          </span>
                          {vote.threshold && (
                            <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              Required: {vote.threshold}
                            </span>
                          )}
                        </div>



                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Bill Text Versions */}
              {selectedBill.texts && selectedBill.texts.length > 0 && (
                <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'} mb-6`}>
                  <h4 className={`text-sm font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-3 uppercase tracking-wide`}>
                    Bill Text Versions ({selectedBill.texts.length})
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {selectedBill.texts.map((text, idx) => (
                      <div key={idx} className="flex items-center justify-between">
                        <div className="flex-1">
                          <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            {text.type}
                          </span>
                          <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            {(text.text_size / 1024).toFixed(1)}KB • PDF
                          </div>
                        </div>
                        <div className="flex gap-2">
                          {text.url && (
                            <a
                              href={text.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={`text-xs px-2 py-1 rounded ${
                                isDarkMode 
                                  ? 'bg-blue-900 text-blue-300 hover:bg-blue-800' 
                                  : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                              } transition-colors`}
                            >
                              LegiScan
                            </a>
                          )}
                          {text.state_link && (
                            <a
                              href={text.state_link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={`text-xs px-2 py-1 rounded ${
                                isDarkMode 
                                  ? 'bg-green-900 text-green-300 hover:bg-green-800' 
                                  : 'bg-green-100 text-green-700 hover:bg-green-200'
                              } transition-colors`}
                            >
                              Official
                            </a>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className={`border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} pt-6 flex flex-wrap gap-3`}>
                {selectedBill.url && (
                  <a
                    href={selectedBill.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-red-600 to-blue-600 text-white font-medium rounded-xl hover:from-red-700 hover:to-blue-700 transition-all duration-300 transform hover:scale-105 hover:shadow-lg"
                  >
                    <ExternalLink className="w-5 h-5 mr-2" />
                    View on LegiScan
                  </a>
                )}
                {selectedBill.state_link && (
                  <a
                    href={selectedBill.state_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`inline-flex items-center px-6 py-3 border-2 ${
                      isDarkMode 
                        ? 'border-gray-600 text-gray-300 hover:border-gray-500' 
                        : 'border-gray-300 text-gray-700 hover:border-gray-400'
                    } font-medium rounded-xl transition-all duration-300 transform hover:scale-105`}
                  >
                    <Scale className="w-5 h-5 mr-2" />
                    View Official Bill
                  </a>
                )}
              </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
};

// Legislative Timeline Component
const LegislativeTimeline = ({ isDarkMode }) => {
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);
  
  const timelineEvents = [
    {
      date: 'Jan 13, 2025',
      legislativeDay: 'Day 1',
      event: 'Session Begins',
      description: 'Official start of the 40-day session',
      icon: Landmark,
      color: 'blue',
      isPast: false,
      isCurrent: false
    },
    {
      date: 'Feb 13, 2025',
      legislativeDay: 'Day 20',
      event: 'Mid-Session Point',
      description: 'Rough halfway through the session',
      icon: Zap,
      color: 'yellow',
      isPast: false,
      isCurrent: false
    },
    {
      date: 'Mar 6, 2025',
      legislativeDay: 'Day 28',
      event: 'Crossover Day',
      description: 'Bills must pass originating chamber to move forward',
      icon: RefreshCw,
      color: 'orange',
      isPast: false,
      isCurrent: false,
      isImportant: true
    },
    {
      date: 'Mar 17, 2025',
      legislativeDay: 'Day 33',
      event: 'Final Committee Deadline',
      description: 'Last day committees can report bills',
      icon: ClipboardList,
      color: 'red',
      isPast: false,
      isCurrent: false,
      isImportant: true
    },
    {
      date: 'Apr 4, 2025',
      legislativeDay: 'Day 40',
      event: 'Sine Die (Session Ends)',
      description: 'Final day of the regular session',
      icon: Flag,
      color: 'purple',
      isPast: false,
      isCurrent: false,
      isImportant: true
    },
    {
      date: 'May 14, 2025',
      legislativeDay: '~40 days post',
      event: "Governor's Deadline",
      description: 'Governor has 40 days post Sine Die to act on bills',
      icon: PenTool,
      color: 'green',
      isPast: false,
      isCurrent: false,
      isImportant: true
    }
  ];

  // Calculate current date status
  const now = new Date();
  const currentEvents = timelineEvents.map(event => {
    const eventDate = new Date(event.date);
    return {
      ...event,
      isPast: eventDate < now,
      isCurrent: Math.abs(eventDate - now) < (7 * 24 * 60 * 60 * 1000) // Within a week
    };
  });

  const getEventColors = (event) => {
    const colorMap = {
      blue: {
        bg: isDarkMode ? 'bg-blue-900/30' : 'bg-blue-50',
        border: isDarkMode ? 'border-blue-700' : 'border-blue-200',
        text: isDarkMode ? 'text-blue-300' : 'text-blue-800',
        dot: 'bg-blue-500'
      },
      yellow: {
        bg: isDarkMode ? 'bg-yellow-900/30' : 'bg-yellow-50',
        border: isDarkMode ? 'border-yellow-700' : 'border-yellow-200',
        text: isDarkMode ? 'text-yellow-300' : 'text-yellow-800',
        dot: 'bg-yellow-500'
      },
      orange: {
        bg: isDarkMode ? 'bg-orange-900/30' : 'bg-orange-50',
        border: isDarkMode ? 'border-orange-700' : 'border-orange-200',
        text: isDarkMode ? 'text-orange-300' : 'text-orange-800',
        dot: 'bg-orange-500'
      },
      red: {
        bg: isDarkMode ? 'bg-red-900/30' : 'bg-red-50',
        border: isDarkMode ? 'border-red-700' : 'border-red-200',
        text: isDarkMode ? 'text-red-300' : 'text-red-800',
        dot: 'bg-red-500'
      },
      purple: {
        bg: isDarkMode ? 'bg-purple-900/30' : 'bg-purple-50',
        border: isDarkMode ? 'border-purple-700' : 'border-purple-200',
        text: isDarkMode ? 'text-purple-300' : 'text-purple-800',
        dot: 'bg-purple-500'
      },
      green: {
        bg: isDarkMode ? 'bg-green-900/30' : 'bg-green-50',
        border: isDarkMode ? 'border-green-700' : 'border-green-200',
        text: isDarkMode ? 'text-green-300' : 'text-green-800',
        dot: 'bg-green-500'
      }
    };

    const colors = colorMap[event.color];
    
    if (event.isPast) {
      // Completed events - keep their vibrant colors but slightly muted backgrounds
      return {
        ...colors,
        bg: colors.bg, // Keep original themed background
        border: colors.border, // Keep original themed border
        text: colors.text, // Keep original themed text
        dot: colors.dot + ' opacity-90' // Keep original dot color, slightly muted
      };
    }
    
    if (event.isCurrent) {
      return {
        ...colors,
        bg: colors.bg,
        border: colors.border + ' ring-4 ring-blue-400 ring-opacity-50',
        dot: colors.dot + ' animate-pulse ring-4 ring-blue-400 ring-opacity-75 shadow-lg shadow-blue-500/50'
      };
    }

    return colors;
  };

  // Get next critical event
  const getNextCriticalEvent = () => {
    const now = new Date();
    return currentEvents.find(event => {
      const eventDate = new Date(event.date);
      return eventDate > now && event.isImportant;
    }) || currentEvents.find(event => {
      const eventDate = new Date(event.date);
      return eventDate > now;
    });
  };

  const nextEvent = getNextCriticalEvent();

  return (
    <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-lg border ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} overflow-hidden transition-all duration-300 ${isExpanded ? 'shadow-xl' : ''}`}>
      {/* Compact Header - Always Visible */}
      <div 
        className={`${isDarkMode ? 'bg-gradient-to-r from-red-900/20 to-blue-900/20' : 'bg-gradient-to-r from-red-50 to-blue-50'} px-4 py-3 cursor-pointer hover:bg-opacity-80 transition-all duration-200`}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className={`flex items-center justify-center w-8 h-8 rounded-full ${isDarkMode ? 'bg-gradient-to-r from-red-600 to-blue-600' : 'bg-gradient-to-r from-red-500 to-blue-500'}`}>
              <Calendar className="w-4 h-4 text-white" />
            </div>
            <div className="flex-1">
              <h3 className={`text-base font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                2025 Georgia Legislative Session
              </h3>
              {!isExpanded && (
                <div className="space-y-2 mt-1">
                  {/* Key dates info */}
                  <div className="flex items-center space-x-4 text-xs">
                    {nextEvent && (
                      <div className="flex items-center space-x-1">
                        <span className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          Next:
                        </span>
                        <span className={`font-medium ${isDarkMode ? 'text-blue-400' : 'text-blue-700'}`}>
                          {nextEvent.event}
                        </span>
                        <span className={`${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                          {nextEvent.date}
                        </span>
                        {nextEvent.isImportant && (
                          <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${
                            isDarkMode ? 'bg-yellow-900/50 text-yellow-300' : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            Critical
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  
                  {/* Mini Timeline Visual */}
                  <div className="flex items-center space-x-1">
                    {currentEvents.map((event, idx) => {
                      const now = new Date();
                      const eventDate = new Date(event.date);
                      const isPastEvent = eventDate < now;
                      const isCurrentEvent = Math.abs(eventDate - now) < (7 * 24 * 60 * 60 * 1000);
                      
                      // Get the color based on event type and status
                      const getEventDotColor = () => {
                        if (isPastEvent) {
                          // Completed events - use their full color (filled in)
                          switch (event.color) {
                            case 'blue': return 'bg-blue-500';
                            case 'yellow': return 'bg-yellow-500';
                            case 'orange': return 'bg-orange-500';
                            case 'red': return 'bg-red-500';
                            case 'purple': return 'bg-purple-500';
                            case 'green': return 'bg-green-500';
                            default: return 'bg-blue-500';
                          }
                        } else if (isCurrentEvent) {
                          // Current event - bright blue with strong pulsing
                          return 'bg-blue-500 animate-pulse ring-2 ring-blue-300 ring-opacity-75';
                        } else if (event.isImportant) {
                          // Future important events - orange
                          return isDarkMode ? 'bg-orange-400' : 'bg-orange-500';
                        } else {
                          // Future regular events - muted
                          return isDarkMode ? 'bg-gray-500' : 'bg-gray-300';
                        }
                      };
                      
                      return (
                        <div key={idx} className="flex items-center">
                          {/* Event dot */}
                          <div className={`relative w-2.5 h-2.5 rounded-full flex-shrink-0 ${getEventDotColor()}`} 
                               title={`${event.event} - ${event.date} ${isPastEvent ? '(Completed)' : isCurrentEvent ? '(Current)' : '(Upcoming)'}`}>
                            {/* Outer pulsing ring for current event */}
                            {isCurrentEvent && (
                              <div className="absolute -inset-1 bg-blue-400 rounded-full opacity-60 animate-ping"></div>
                            )}
                            {/* Yellow indicator dot for important future events */}
                            {event.isImportant && !isPastEvent && !isCurrentEvent && (
                              <div className="absolute -top-0.5 -right-0.5 w-1 h-1 bg-yellow-400 rounded-full animate-pulse"></div>
                            )}
                            {/* Checkmark for completed events */}
                            {isPastEvent && (
                              <div className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 bg-green-400 rounded-full flex items-center justify-center">
                                <div className="w-0.5 h-0.5 bg-white rounded-full"></div>
                              </div>
                            )}
                          </div>
                          {/* Connecting line (except for last item) */}
                          {idx < currentEvents.length - 1 && (
                            <div className={`w-4 h-0.5 ${
                              isPastEvent 
                                ? 'bg-green-400' // Completed path - green
                                : isCurrentEvent
                                  ? 'bg-blue-400' // Current path - blue
                                  : 'bg-gray-500' // Future path - gray
                            }`}></div>
                          )}
                        </div>
                      );
                    })}
                    <div className={`ml-2 text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                      Jan 13 - Apr 4
                    </div>
                  </div>
                </div>
              )}
              {isExpanded && (
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  40-day regular session timeline
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center space-x-3">
            {!isExpanded && nextEvent && (
              <div className={`px-2 py-1 rounded text-xs font-medium ${
                nextEvent.isImportant 
                  ? isDarkMode ? 'bg-orange-900/50 text-orange-300' : 'bg-orange-100 text-orange-800'
                  : isDarkMode ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-100 text-blue-800'
              }`}>
                {(() => {
                  const eventDate = new Date(nextEvent.date);
                  const today = new Date();
                  const diffTime = eventDate - today;
                  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                  return diffDays === 1 ? 'Tomorrow' : `${diffDays} days`;
                })()}
              </div>
            )}
            <div className={`transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}>
              <svg className={`w-4 h-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Expandable Timeline */}
      {isExpanded && (
        <div className="border-t border-gray-200 dark:border-gray-700">
          <div className="p-6 animate-in slide-in-from-top-2 duration-300">
            <div className="relative overflow-x-auto">
              {/* Horizontal line */}
              <div className="absolute top-8 left-0 right-0 h-0.5 bg-gray-300 dark:bg-gray-700 z-0" style={{ minWidth: '600px' }}></div>
              <div className="flex flex-nowrap items-start space-x-12 relative z-10" style={{ minWidth: '600px' }}>
                {currentEvents.map((event, idx) => {
                  const colors = getEventColors(event);
                  return (
                    <div key={idx} className="flex flex-col items-center min-w-[140px] max-w-[180px] flex-shrink-0">
                      {/* Icon */}
                      <div className={`w-12 h-12 rounded-full border-4 flex items-center justify-center text-white font-bold shadow-lg mb-2 ${colors.dot} ${isDarkMode ? 'border-gray-800' : 'border-white'}`}>{
                        React.createElement(event.icon, { className: 'w-6 h-6' })
                      }</div>
                      {/* Event name */}
                      <div className={`text-sm font-bold mb-1 text-center ${colors.text}`}>{event.event}</div>
                      {/* Date and day */}
                      <div className={`text-xs mb-1 text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{event.date}</div>
                      <div className={`text-xs mb-1 text-center ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>{event.legislativeDay}</div>
                      {/* Description */}
                      <div className={`text-xs text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{event.description}</div>
                  </div>
                  );
                })}
                        </div>
                        </div>
                      </div>
        </div>
      )}
    </div>
  );
};

// Enhanced Directional Scale Partisan Score Calculation (Signed System)
const calculatePartisanScore = (bill) => {
  // 1. Identify the Final Major Floor Vote
  let finalVote = null;
  if (bill.voteBreakdowns && bill.voteBreakdowns.length > 0) {
    // Find votes with final passage/third reading/passed
    const finalVotes = bill.voteBreakdowns.filter(vb => {
      const desc = (vb.description || '').toLowerCase();
      return desc.includes('final passage') || desc.includes('third reading') || desc.includes('passed');
    });
    // Use the latest one if multiple
    if (finalVotes.length > 0) {
      finalVote = finalVotes.reduce((latest, vb) => {
        if (!latest) return vb;
        const d1 = new Date(latest.date || 0);
        const d2 = new Date(vb.date || 0);
        return d2 > d1 ? vb : latest;
      }, null);
    }
  }
  // If not found, fallback to most recent vote
  if (!finalVote && bill.voteBreakdowns && bill.voteBreakdowns.length > 0) {
    finalVote = bill.voteBreakdowns.reduce((latest, vb) => {
      if (!latest) return vb;
      const d1 = new Date(latest.date || 0);
      const d2 = new Date(vb.date || 0);
      return d2 > d1 ? vb : latest;
    }, null);
  }

  // 2. Calculate Sponsor Score (signed)
  let sponsorScore = 0;
  let democratSponsors = 0, republicanSponsors = 0;
  if (bill.sponsors && bill.sponsors.length > 0) {
    bill.sponsors.forEach(s => {
      const party = (s.party || '').toUpperCase();
      if (party === 'D' || party === 'DEM' || party === 'DEMOCRAT') democratSponsors++;
      else if (party === 'R' || party === 'REP' || party === 'REPUBLICAN') republicanSponsors++;
    });
    const total = democratSponsors + republicanSponsors;
    if (total > 0) {
      const democratPercent = (democratSponsors / total) * 100;
      const republicanPercent = (republicanSponsors / total) * 100;
      // Signed score: negative for Democratic, positive for Republican
      sponsorScore = republicanPercent - democratPercent;
    }
  }

  // 3. Calculate Vote Score (signed)
  let voteScore = 0;
  let demYeaPercent = 0, repYeaPercent = 0;
  let hasVoteData = false;
  if (finalVote && finalVote.partyVoteCounts) {
    const dem = finalVote.partyVoteCounts.Democrat || {};
    const rep = finalVote.partyVoteCounts.Republican || {};
    const demTotal = (dem.Yea || 0) + (dem.Nay || 0) + (dem.NV || 0) + (dem.Absent || 0) + (dem.Excused || 0);
    const repTotal = (rep.Yea || 0) + (rep.Nay || 0) + (rep.NV || 0) + (rep.Absent || 0) + (rep.Excused || 0);
    
    if (demTotal > 0 && repTotal > 0) {
      demYeaPercent = (dem.Yea || 0) / demTotal * 100;
      repYeaPercent = (rep.Yea || 0) / repTotal * 100;
      // Signed score: negative for Democratic, positive for Republican
      voteScore = repYeaPercent - demYeaPercent;
      hasVoteData = true;
    }
  }

  // 4. Combine for the Final Partisan Score (signed)
  let partisanScore;
  if (hasVoteData) {
    partisanScore = 0.5 * sponsorScore + 0.5 * voteScore;
  } else {
    partisanScore = sponsorScore;
  }
  
  const roundedScore = Math.round(partisanScore * 10) / 10;
  const absoluteScore = Math.abs(roundedScore);

  // 5. Determine Direction from Sign
  let direction = 'Neutral';
  if (roundedScore < 0) {
    direction = 'Democratic';
  } else if (roundedScore > 0) {
    direction = 'Republican';
  }

  // 6. Enhanced Bucketed Label System (based on absolute score)
  let label = '';
  let leanStrength = '';
  // Only absolute values 0-10 are bipartisan
  if (absoluteScore <= 10) {
    leanStrength = 'Bipartisan';
    label = '🟨 Bipartisan';
  } else if (absoluteScore <= 30) {
    leanStrength = 'Leaning';
    if (direction === 'Democratic') {
      label = '🟨 Leaning Democrat';
    } else if (direction === 'Republican') {
      label = '🟨 Leaning Republican';
    } else {
      label = '🟨 Leaning Neutral';
    }
  } else if (absoluteScore <= 60) {
    leanStrength = 'Moderately';
    if (direction === 'Democratic') {
      label = '🟦 Moderately Democrat';
    } else if (direction === 'Republican') {
      label = '🟥 Moderately Republican';
    } else {
      label = '🟨 Mixed Partisan';
    }
  } else if (absoluteScore <= 80) {
    leanStrength = 'Strongly';
    if (direction === 'Democratic') {
      label = '🟦 Strongly Democrat';
    } else if (direction === 'Republican') {
      label = '🟥 Strongly Republican';
    } else {
      label = '🟨 Mixed Partisan';
    }
  } else {
    leanStrength = 'Highly';
    if (direction === 'Democratic') {
      label = '🟦 Highly Democrat';
    } else if (direction === 'Republican') {
      label = '🟥 Highly Republican';
    } else {
      label = '🟨 Mixed Partisan';
    }
  }

  return {
    score: absoluteScore, // Return absolute value for display
    signedScore: roundedScore, // Keep signed version for internal use
    label,
    direction,
    leanStrength,
    sponsorScore: Math.round(sponsorScore * 10) / 10,
    voteScore: hasVoteData ? Math.round(voteScore * 10) / 10 : null,
    democratSponsors,
    republicanSponsors,
    demYeaPercent: hasVoteData ? Math.round(demYeaPercent * 10) / 10 : null,
    repYeaPercent: hasVoteData ? Math.round(repYeaPercent * 10) / 10 : null,
    hasVoteData
  };
};

// Memoized Bill Card Component
const BillCard = React.memo(({ 
  bill, 
  isDarkMode, 
  onBillClick, 
  formatDate, 
  getStatusColor, 
  getPolicyTagIcon, 
  getPolicyTagColor,
  getDaysActive
}) => {
  const partisanData = calculatePartisanScore(bill);
  const mainSponsor = bill.sponsors && bill.sponsors.length > 0 ? bill.sponsors[0] : null;

  // Color logic for partisan label
  const getPartisanLabelColor = () => {
    if (!partisanData || !partisanData.label) return 'bg-gray-100 text-gray-700 border-gray-200';
    const label = partisanData.label.toLowerCase();
    if (label.includes('bipartisan') || label.includes('neutral')) {
      return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    }
    if (label.includes('democratic')) {
      if (label.includes('highly')) return 'bg-blue-700 text-white border-blue-800';
      if (label.includes('strongly')) return 'bg-blue-500 text-white border-blue-600';
      if (label.includes('moderately')) return 'bg-blue-200 text-blue-800 border-blue-300';
      if (label.includes('leaning')) return 'bg-blue-100 text-blue-700 border-blue-200';
    }
    if (label.includes('republican')) {
      if (label.includes('highly')) return 'bg-red-700 text-white border-red-800';
      if (label.includes('strongly')) return 'bg-red-500 text-white border-red-600';
      if (label.includes('moderately')) return 'bg-red-200 text-red-800 border-red-300';
      if (label.includes('leaning')) return 'bg-red-100 text-red-700 border-red-200';
    }
    // fallback
    return 'bg-gray-100 text-gray-700 border-gray-200';
  };

  // Calculate marker position for the partisan bar
  const partisanBarPercent = partisanData && typeof partisanData.signedScore === 'number'
    ? ((partisanData.signedScore + 100) / 200) * 100
    : 50;

  return (
    <div
      onClick={() => onBillClick(bill)}
      className={`group relative bg-transparent rounded-xl hover:shadow-lg transition-all duration-300 cursor-pointer border ${isDarkMode ? 'border-gray-700 hover:border-blue-600' : 'border-gray-200 hover:border-blue-200'}`}
      style={{padding: '1.5rem 2rem', marginBottom: '1.5rem'}}
    >
      {/* Top Row: Bill number, status, date */}
      <div className="flex items-start justify-between gap-4 mb-2">
        <div className="flex items-center gap-3 flex-shrink-0">
          <h3 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{bill.bill_number}</h3>
          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(bill.currentStatus.status)}`}>{bill.currentStatus.label}</span>
          <div className={`flex items-center text-xs ml-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`}> <Calendar className="w-4 h-4 mr-1" /> <span>{formatDate(bill.mostRecentDate)}</span> </div>
        </div>
        <span className={`text-xs ml-3 whitespace-nowrap self-center ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`}>{bill.session_name}</span>
      </div>
      {/* Title */}
      <div className={`text-[1.25rem] font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{getReadableTitle(bill)}</div>
      {/* Summary/Description */}
      <div className={`text-base mb-5 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{bill.ai_summary || bill.description || 'No summary available.'}</div>
      {/* Bottom Row: Policy Tags + Gauge (left), Sponsors (right, bottom) */}
      <div className="flex flex-row mt-4 items-end">
        {/* Policy Tags and Partisan Gauge - left, take up available space */}
        <div className="flex items-center gap-2 flex-1">
          {bill.policyTags && bill.policyTags.length > 0 && (
            bill.policyTags.map((tag, idx) => {
              const IconComponent = getPolicyTagIcon(tag);
              return (
                <span key={idx} className={`inline-flex items-center px-3 py-1.5 rounded-[0.75rem] text-sm font-semibold shadow-sm border ${
                  isDarkMode 
                    ? 'bg-pink-900/50 text-pink-300 border-pink-700' 
                    : 'bg-pink-100 text-pink-700 border-pink-200'
                }`}>
                  <IconComponent className="w-4 h-4 mr-1.5" />
                  {tag}
                </span>
              );
            })
          )}
          {/* Small Semicircle Partisan Gauge - right of tags, inline */}
          <div
                          className={`inline-flex flex-col items-center ml-3 px-3 py-1 transition-colors duration-150 ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}
              style={{ marginTop: 0, minWidth: '90px' }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', gap: 1 }}>
                {/* Arc gauge - horseshoe shape that fills from top center */}
                <div style={{ position: 'relative', width: 45, height: 45 }}>
                  {/* Background arc - complete horseshoe shape */}
                  <div style={{
                    position: 'absolute',
                    width: '100%',
                    height: '100%',
                    borderRadius: '50%',
                    background: '#e5e7eb',
                    mask: `
                      radial-gradient(circle, transparent 40%, black 40%, black 60%, transparent 60%),
                      conic-gradient(from 225deg, black 0deg, black 270deg, transparent 270deg, transparent 360deg, black 360deg)
                    `,
                    WebkitMask: `
                      radial-gradient(circle, transparent 40%, black 40%, black 60%, transparent 60%),
                      conic-gradient(from 225deg, black 0deg, black 270deg, transparent 270deg, transparent 360deg, black 360deg)
                    `,
                    maskComposite: 'intersect',
                    WebkitMaskComposite: 'intersect',
                    zIndex: 1
                  }} />
                
                {/* Democratic progress arc (counterclockwise from top center) */}
                {partisanData && partisanData.signedScore < 0 && (() => {
                  const absScore = Math.abs(partisanData.signedScore);
                  const fillPercentage = Math.min(absScore, 100); // 0-100%
                  const gradientStop = Math.min(absScore, 100);
                  
                  // Calculate the color at the gradient stop
                  const lightBlue = { r: 147, g: 197, b: 253 }; // #93c5fd
                  const darkBlue = { r: 30, g: 64, b: 175 }; // #1e40af
                  const ratio = gradientStop / 100;
                  const endColor = {
                    r: Math.round(lightBlue.r + (darkBlue.r - lightBlue.r) * ratio),
                    g: Math.round(lightBlue.g + (darkBlue.g - lightBlue.g) * ratio),
                    b: Math.round(lightBlue.b + (darkBlue.b - lightBlue.b) * ratio)
                  };
                  const endColorHex = `rgb(${endColor.r}, ${endColor.g}, ${endColor.b})`;
                  
                  // Arc spans 135 degrees (from top center 270deg counterclockwise to 135deg)
                  // 100% = 135 degrees of rotation
                  const arcDegrees = fillPercentage * 1.35; // 135deg / 100 = 1.35deg per percent
                  
                  return (
                    <div style={{
                      position: 'absolute',
                      width: '100%',
                      height: '100%',
                      borderRadius: '50%',
                      background: `conic-gradient(from 0deg at 50% 50%, #93c5fd 0deg, ${endColorHex} ${arcDegrees}deg, transparent ${arcDegrees}deg)`,
                      mask: `
                        radial-gradient(circle, transparent 40%, black 40%, black 60%, transparent 60%),
                        conic-gradient(from 225deg, black 0deg, black 270deg, transparent 270deg, transparent 360deg, black 360deg)
                      `,
                      WebkitMask: `
                        radial-gradient(circle, transparent 40%, black 40%, black 60%, transparent 60%),
                        conic-gradient(from 225deg, black 0deg, black 270deg, transparent 270deg, transparent 360deg, black 360deg)
                      `,
                      maskComposite: 'intersect',
                      WebkitMaskComposite: 'intersect',
                      zIndex: 2,
                      transition: 'background 0.5s cubic-bezier(0.4,0,0.2,1)',
                      transform: 'scaleX(-1)' // Flip horizontally to make it go counterclockwise
                    }} />
                  );
                })()}
                
                {/* Republican progress arc (clockwise from top center) */}
                {partisanData && partisanData.signedScore > 0 && (() => {
                  const absScore = Math.abs(partisanData.signedScore);
                  const fillPercentage = Math.min(absScore, 100); // 0-100%
                  const gradientStop = Math.min(absScore, 100);
                  
                  // Calculate the color at the gradient stop
                  const lightRed = { r: 252, g: 165, b: 165 }; // #fca5a5
                  const darkRed = { r: 185, g: 28, b: 28 }; // #b91c1c
                  const ratio = gradientStop / 100;
                  const endColor = {
                    r: Math.round(lightRed.r + (darkRed.r - lightRed.r) * ratio),
                    g: Math.round(lightRed.g + (darkRed.g - lightRed.g) * ratio),
                    b: Math.round(lightRed.b + (darkRed.b - lightRed.b) * ratio)
                  };
                  const endColorHex = `rgb(${endColor.r}, ${endColor.g}, ${endColor.b})`;
                  
                  // Arc spans 135 degrees (from top center 270deg clockwise to 45deg)
                  // 100% = 135 degrees of rotation
                  const arcDegrees = fillPercentage * 1.35; // 135deg / 100 = 1.35deg per percent
                  
                  return (
                    <div style={{
                      position: 'absolute',
                      width: '100%',
                      height: '100%',
                      borderRadius: '50%',
                      background: `conic-gradient(from 0deg at 50% 50%, #fca5a5 0deg, ${endColorHex} ${arcDegrees}deg, transparent ${arcDegrees}deg)`,
                      mask: `
                        radial-gradient(circle, transparent 40%, black 40%, black 60%, transparent 60%),
                        conic-gradient(from 225deg, black 0deg, black 270deg, transparent 270deg, transparent 360deg, black 360deg)
                      `,
                      WebkitMask: `
                        radial-gradient(circle, transparent 40%, black 40%, black 60%, transparent 60%),
                        conic-gradient(from 225deg, black 0deg, black 270deg, transparent 270deg, transparent 360deg, black 360deg)
                      `,
                      maskComposite: 'intersect',
                      WebkitMaskComposite: 'intersect',
                      zIndex: 2,
                      transition: 'background 0.5s cubic-bezier(0.4,0,0.2,1)'
                    }} />
                  );
                })()}
                
                {/* Numerical score in the center of the arc */}
                {partisanData && (() => {
                  let fillColor = '#64748b'; // Default neutral color
                  
                  if (partisanData.signedScore < 0) {
                    // Calculate Democratic color based on score intensity
                    const absScore = Math.abs(partisanData.signedScore);
                    const gradientStop = Math.min(absScore, 100);
                    const lightBlue = { r: 147, g: 197, b: 253 }; // #93c5fd
                    const darkBlue = { r: 30, g: 64, b: 175 }; // #1e40af
                    const ratio = gradientStop / 100;
                    const endColor = {
                      r: Math.round(lightBlue.r + (darkBlue.r - lightBlue.r) * ratio),
                      g: Math.round(lightBlue.g + (darkBlue.g - lightBlue.g) * ratio),
                      b: Math.round(lightBlue.b + (darkBlue.b - lightBlue.b) * ratio)
                    };
                    fillColor = `rgb(${endColor.r}, ${endColor.g}, ${endColor.b})`;
                  } else if (partisanData.signedScore > 0) {
                    // Calculate Republican color based on score intensity
                    const absScore = Math.abs(partisanData.signedScore);
                    const gradientStop = Math.min(absScore, 100);
                    const lightRed = { r: 252, g: 165, b: 165 }; // #fca5a5
                    const darkRed = { r: 185, g: 28, b: 28 }; // #b91c1c
                    const ratio = gradientStop / 100;
                    const endColor = {
                      r: Math.round(lightRed.r + (darkRed.r - lightRed.r) * ratio),
                      g: Math.round(lightRed.g + (darkRed.g - lightRed.g) * ratio),
                      b: Math.round(lightRed.b + (darkRed.b - lightRed.b) * ratio)
                    };
                    fillColor = `rgb(${endColor.r}, ${endColor.g}, ${endColor.b})`;
                  }
                  
                  return (
                    <div style={{ 
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      fontWeight: 700, 
                      fontSize: String(Math.abs(partisanData.signedScore)).includes('.') ? 9 : 11,
                      color: fillColor,
                      textAlign: 'center',
                      lineHeight: 1,
                      zIndex: 4
                    }}>
                      {Math.abs(partisanData.signedScore)}
        </div>
                  );
                })()}
              </div>
              
              {/* Partisan label at the bottom, single line and centered */}
              {partisanData && partisanData.label && (() => {
                let fillColor = '#64748b'; // Default neutral color
                
                if (partisanData.signedScore < 0) {
                  // Calculate Democratic color based on score intensity
                  const absScore = Math.abs(partisanData.signedScore);
                  const gradientStop = Math.min(absScore, 100);
                  const lightBlue = { r: 147, g: 197, b: 253 }; // #93c5fd
                  const darkBlue = { r: 30, g: 64, b: 175 }; // #1e40af
                  const ratio = gradientStop / 100;
                  const endColor = {
                    r: Math.round(lightBlue.r + (darkBlue.r - lightBlue.r) * ratio),
                    g: Math.round(lightBlue.g + (darkBlue.g - lightBlue.g) * ratio),
                    b: Math.round(lightBlue.b + (darkBlue.b - lightBlue.b) * ratio)
                  };
                  fillColor = `rgb(${endColor.r}, ${endColor.g}, ${endColor.b})`;
                } else if (partisanData.signedScore > 0) {
                  // Calculate Republican color based on score intensity
                  const absScore = Math.abs(partisanData.signedScore);
                  const gradientStop = Math.min(absScore, 100);
                  const lightRed = { r: 252, g: 165, b: 165 }; // #fca5a5
                  const darkRed = { r: 185, g: 28, b: 28 }; // #b91c1c
                  const ratio = gradientStop / 100;
                  const endColor = {
                    r: Math.round(lightRed.r + (darkRed.r - lightRed.r) * ratio),
                    g: Math.round(lightRed.g + (darkRed.g - lightRed.g) * ratio),
                    b: Math.round(lightRed.b + (darkRed.b - lightRed.b) * ratio)
                  };
                  fillColor = `rgb(${endColor.r}, ${endColor.g}, ${endColor.b})`;
                }
                
                // Change "Democratic" to "Democrat" in the label
                const labelText = partisanData.label.replace(/^[^ ]+ /, '').replace('Democratic', 'Democrat');
                
                return (
                  <div style={{ 
                    fontSize: 9, 
                    fontWeight: 600, 
                    color: fillColor, 
                    marginTop: 0,
                    textAlign: 'center', 
                    lineHeight: 1,
                    whiteSpace: 'nowrap',
                    overflow: 'visible',
                    width: '100%'
                  }}>
                    {labelText}
                  </div>
                );
              })()}
            </div>
          </div>
          {/* Sponsors - bottom right, always, not squished */}
          <div className={`flex items-center gap-0.5 text-xs whitespace-nowrap ml-auto self-end ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          <Users className={`w-4 h-4 mr-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`} />
          <span className="font-medium">{bill.sponsors?.length || 0} sponsors</span>
          {mainSponsor && (
            <>
              <span className="mx-0.5">·</span>
              <span className={`font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{mainSponsor.name}</span>
            </>
          )}
          {bill.sponsors && bill.sponsors.length > 1 && (
            <span className={`ml-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`}>+{bill.sponsors.length - 1} more</span>
          )}
          </div>
        </div>
      </div>
    </div>
  );
});

export default Bills; 